import { RobocloudEventType } from '@robocorp/robocloud-common/lib/types/RobocloudEvents';
import { WorkspaceRoleId } from '@robocorp/robocloud-internal-cds-sdk';
import {
  ProcessRunState,
  ProcessRunResult,
  FailedWorkItemExceptionType,
  ActivityRunState,
  ActivityRunResult,
  WorkItemLogEventType,
} from '@robocorp/robocloud-orchestrator-interface';
import {
  OutgoingEventType,
  ProcessWebhookEventType,
  WebhookType,
} from '@robocorp/robocloud-webhooks-interface';
import { UploadStatus } from '@robocorp/robocloud-asset-storage-interface';
import { LinkGitlabSourceMutationErrors } from '~/queries/robots/useLinkGitlabSourceMutation';
import { AssetContent } from '~/schemas/assetStorageSchemas';
import { DefaultApiError } from '~/utils/api/error';
import { OnboardingProcessTypes } from '~/hooks';
import { UserActionType } from '../actions/userActions';
import { ProcessScheduleActionType } from '../actions/processScheduleActions';
import { ActivityRunActionType } from '../actions/activityRunActions';
import { ArtifactActionType } from '../actions/artifactActions';
import { ContainerImageActionType } from '../actions/containerImageActions';
import {
  processRunErrorCodes,
  activityRunEvents,
  runtimeStates,
  ErrorSubCodes,
  RuntimeType,
  UserMessages,
  OrganizationRoleNames,
  ErrorActions,
  availabilityStates,
} from '../constants';
import { ActivityActionType } from '../actions/activityActions';
import { ApiKeyActionType } from '../actions/apiKeyActions';
import { SecretActionType } from '../actions/secretActions';
import { ProcessActionType } from '../actions/processActions';
import { ActivityTemplateActionType } from '../actions/activityTemplateActions';
import { BillingActionType } from '../actions/billingActions';
import { FeedbackActionType } from '../actions/feedbackActions';
import { ServiceDeskActionType } from '../actions/serviceDeskActions';
import { WorkspaceKeysActionType } from '../actions/workspaceKeyActions';
import {
  WorkspacesQueryErrors,
  WorkspaceInvitesQueryErrors,
  WorkspaceMembersQueryErrors,
  OrganizatioinsQueryErrors,
  OrganizationInvitesErrors,
  OrganizationMembersErrors,
  ProcessRunsErrors,
  WorkItemSummaryErrors,
  WorkItemsQueryErrors,
  LinkGithubSourceMutationErrors,
  SsoManagementQueryErrors,
  EmailConfigurationErrors,
  RobotsQueryErrors,
  MFA_CONFIGURATION_ERROR,
} from '../queries';
import {
  WorkItemState,
  EnvGroupTokenExpiration,
  ScheduleType,
  AssistantRunFullState,
  AssistantRunFullResult,
} from '../types/resources';

export default {
  translation: {
    editDescription: 'Edit description',
    submitIssue: 'Submit Issue',
    new: 'New',
    newWorkspace: 'New Workspace',
    startUsingAgents: 'Start using agents',
    workingAs: 'Working as',
    workspace: 'Workspace',
    workspaceDesc: 'No data is shared between workspaces.',
    workspaces: 'Workspaces',
    workspaceId: 'Workspace ID',
    welcome: 'Welcome!',
    welcomeWithName: 'Welcome {{ name }}!',
    ssoPlaceholderDesc: {
      intro: 'It appears that you do not have permissions set to access Control Room.',
      content:
        'This can be because the SSO has been just created and no roles have been assigned yet.',
      help: 'In case this is is unexpected, please reach out to <0>Sema4.ai</0> support.',
    },
    noOrganizationsDesc: 'Accept an invitation or create your own organization to get started.',
    noOrganizationsNoInvitesDesc: 'Create your own organization to get started.',
    privateWorkspaceId: 'Private Workspace ID',
    userId: 'User ID',
    workItemId: 'Work Item ID',
    queueWorkItems: 'Work items in queue',
    finishedWorkItems: 'Finished work items',
    workItemData: 'Work item data',
    payload: 'Payload',
    name: 'Name',
    alias: 'Alias',
    delete: 'Delete',
    delete_plural: 'Delete {{ count }}',
    rename: 'Rename',
    wsActions: 'Workspace actions',
    yourself: 'Yourself',
    runtimeActions: 'Worker actions',
    file: 'File',
    runDetails: 'Run details',
    runArtifacts: 'Run artifacts',
    id: 'ID',
    date: 'Date',
    in: 'In',
    workersDesc: 'A worker is a computing resource that can execute RPA tasks.',
    linkedWorkers: 'Linked workers',
    start: 'Start',
    runProcess: 'Run Process',
    shortName: 'Short name',
    logout: 'Logout',
    settingsAndMembers: 'Configuration',
    preferences: 'Preferences',
    integrations: 'Integrations',
    permissions: 'Permissions',
    preferencesAndNotifications: 'Preferences & Notifications',
    bytes: 'Bytes',
    login: 'Login',
    changeFile: 'Change file',
    selectFile: 'Select file',
    upload: 'Upload',
    size: 'Size',
    lastModified: 'Last modified',
    uploaded: 'Uploaded',
    uploadUnknownError: 'It was not possible to upload this file',
    failedToUploadBundle: 'Uploaded file is not valid',
    noBundleMetaDataFound: 'No robot metadata found',
    robotFile: 'Robot file',
    waitingForBundleText: 'File processing could take a while',
    waitingForBundle: 'Processing robot file',
    gitBundleNote: 'Note: Current robot file is downloaded from Git',
    console: 'Console',
    eventCategory: 'Event category',
    timestamp: 'Timestamp',
    timerange: 'Timerange',
    inviteByEmail: 'Invite user to workspace',
    addByEmail: 'Add user to workspace by email',
    inviteToOrgByEmail: 'Invite user to organization',
    invite: 'Invite',
    invitations: 'Invitations',
    emptyInviteList: 'There are no pending invites',
    accept: 'Accept',
    decline: 'Decline',
    wsJoinConfirm: 'Confirm workspace invite',
    invalidInvite: 'Invalid invite',
    invalidInviteDescription:
      'This invite may have expired, or you might not have permission to join',
    wsInvite: 'Workspace invite',
    changeWorkspace: 'Change workspace',
    user: 'User',
    workspaceName: 'Workspace name',
    currentWorkspace: 'Current workspace',
    createWorkspace: 'Create Workspace',
    createNewWorkspace: 'Create a new workspace',
    createNewWorkspaceDescription:
      'Workspace is a collection of related processes, configuration and results. Use workspaces for organizing your processes and for managing user access.',
    yourPrivateWS: 'Your private workspace',
    userSettings: 'Account settings',
    profileSettings: 'User settings',
    workspaceSettings: 'Workspace settings',
    wsMembers: 'Workspace members',
    userDetails: 'User details',
    firstName: 'First name',
    lastName: 'Last name',
    companyName: 'Company name',
    email: 'Email',
    noName: 'No name',
    noEmail: 'No email',
    addEmail: 'Add email address',
    currentlyLoggedInAs: 'You are currently logged in as',
    orgPicUrl: 'Picture URL',
    errorBoundary1: 'Oops... something went wrong',
    errorBoundaryText: 'The engineering team has been informed. If the problem persists',
    created: 'Created',
    updated: 'Updated',
    createdAt: 'Created at',
    updatedAt: 'Updated at',
    lastAccessed: 'Last Accessed',
    robotActions: 'Robot actions',
    robotRuns: 'Runs',
    config: 'Configuration',
    runSettings: 'Run Settings',
    command: 'Command',
    commandHelper: 'A command to execute from the task package file (for example “main.sh”).',
    attachRuntime: 'Attach worker',
    detach: 'Detach',
    outgoingInvites: 'Outgoing workspace invitations',
    theme: {
      appearance: 'Appearance',
      system: 'Use system setting',
      light: 'Light theme',
      dark: 'Dark theme',
    },
    english: 'English',
    finnish: 'Finnish',
    selectLanguage: 'Select language',
    dayMon: 'Monday',
    dayTue: 'Tuesday',
    dayWed: 'Wednesday',
    dayThu: 'Thursday',
    dayFri: 'Friday',
    daySat: 'Saturday',
    daySun: 'Sunday',
    weekdaySchedule: { unspecifiedDays: 'Unspecified days' },
    schedule: 'Schedule',
    intervalInMinutes: 'Interval in minutes',
    add: 'Add',
    edit: 'Edit',
    [ScheduleType.Interval]: 'Interval',
    [ScheduleType.Cron]: 'Cron',
    scheduler: 'Scheduler',
    type: 'Type',
    linkKeyText: 'Key',
    consoleOutput: 'Console output',
    scheduleWeekday: 'Weekday',
    scheduleInterval: 'Interval',
    deletingSchedule: 'Deleting schedule',
    addIntervalSchedule: 'Add interval schedule',
    addWeekdaySchedule: 'Add weekday schedule',
    intervalDesc:
      'Process is started after the interval time has passed since the end of the last run',
    weekdayDesc: 'Specify starting weekdays and time',
    weekdayShortLength: 3,
    timezone: 'Timezone',
    save: 'Save',
    time: 'Time',
    weekday: 'Weekday',
    editWeekdaySchedule: 'Edit weekday schedule',
    [runtimeStates.OFFLINE]: 'Offline',
    [runtimeStates.RECONNECTING]: 'Reconnecting',
    [runtimeStates.IDLE]: 'Idle',
    [runtimeStates.BUSY]: 'Busy',
    [availabilityStates.ENABLED]: 'Enabled',
    [availabilityStates.DISABLED]: 'Disabled',
    cancel: 'Cancel',
    confirm: 'Confirm',
    saveChanges: 'Save changes',
    deleteSchedule: 'Delete schedule',
    deleteScheduleText: 'Are you sure you want to delete this schedule?',
    deleteRobot: 'Delete task package "{{robotName}}"',
    deleteRobotText:
      'Are you sure you want to delete the task package "{{robotName}}"? Please note that deleting this also causes the deletion of any assistants that used task package.',
    deleteRuntime: 'Delete worker',
    deleteRuntimeText: 'Are you sure you want to delete this worker?',
    deleteWorkspace: 'Delete workspace',
    deleteWorkspaceText:
      'Are you sure you want to delete this workspace? This will also remove all workspace related data. This action cannot be reverted.',
    deleteWorkspaceText2:
      'Delete the workspace and all data associated with it. This action cannot be reverted.',
    deleteWorkspaceData: 'Delete workspace data',
    deleteWorkspaceDataText:
      'Are you sure you want to delete all the data in this workspace? This action cannot be reverted.',
    deleteWorkspaceDataText2:
      'Delete all data associated with the workspace. This action cannot be reverted.',
    deleteWorkspaceMember: 'Remove workspace member',
    deleteWorkspaceMemberText: 'Are you sure you want to remove this workspace member?',
    deleteWorkspaceMemberTextSelf:
      'Are you sure you want to leave the workspace? You are the last member of this workspace',
    deleteWorkspaceInvite: 'Cancel workspace invite',
    deleteWorkspaceInviteText: 'Are you sure you want to cancel this workspace invite?',
    deleteWorkItemData: 'Delete work item data',
    deleteWorkItemDataText: 'Are you sure you want to delete this work item data?',
    deleteWorkItem: 'Delete work item',
    deleteWorkItemText: 'Are you sure you want to delete this work item?',
    changeRobotType: 'Change task package type',
    changeRobotTypeText:
      'Are you sure you want to change task package type? This change may break your existing processes.',
    fetchingToken: 'Generating link token',
    generateToken: 'Generate link token',
    regenerateToken: 'Regenerate link token',
    copyToClipboard: 'Copy to clipboard',
    copied: 'Copied',
    copyValueToClipboard: 'Copy "{{value}}" to clipboard',
    copiedValueToClipboard: 'Copied "{{value}}" to clipboard',
    deletingRobot: 'Deleting robot',
    changingRuntime: 'Changing worker',
    attach: 'Attach',
    attachingRuntime: 'Attaching worker',
    detachingRuntime: 'Detaching worker',
    detachRuntimeText: 'Are you sure you want to detach this worker?',
    detachRuntime: 'Detach Worker',
    selectRuntime: 'Select Worker',
    startTime: 'Start time',
    expireTime: 'Expire time',
    started: 'Started',
    duration: 'Duration',
    result: 'Result',
    deletingRuntime: 'Deleting Worker',
    state: 'State',
    onlineSince: 'Online since',
    engineRoom: 'Engine room',
    latestStateUpdate: 'Latest state update',
    os: 'Operating System',
    versions: 'Versions',
    noConnection: 'No connection',
    unknown: 'Unknown',
    run: 'Run',
    runs: 'Runs',
    create: 'Create',
    modify: 'Modify',
    workspaceInvites: 'Workspace invites',
    deletingWorkspace: 'Deleting workspace',
    join: 'Join',
    joinThisWorkspace: 'Join this workspace as admin',
    workspaceCreateDescription:
      'Workspace is a collection of related processes, configuration and results. Use workspaces for organizing your processes and for managing user access.',
    workspaceCreateDescriptionShort:
      'Create a workspace to organize processes and manage user access',
    downloadBundle: 'Download robot file',
    bundle: 'Robot File',
    attachedRobots: 'Attached robots',
    getBundleMeta: 'Fetching robot metadata',
    bundleLastEdit: 'Latest robot modification',
    rcVersions: 'Control Room versions',
    workforceAgent: 'Workforce agent',
    agentVersions: 'Worker agent internal versions',
    endTime: 'End time',
    success: 'Success',
    error: 'Error',
    activeProcesses: 'Active processes',
    finishedProcesses: 'Finished processes',
    details: 'Details',
    processName: 'Process name',
    configureProcess: 'Configure Process',
    occupiedBy: 'Occupied by',
    billedSeconds: 'Billed seconds',
    runNumber: 'Run number',
    update: 'Update',
    runTimeout: 'Timeout before run is terminated (s)',
    events: 'Events',
    durationSeconds: 'Duration (s)',
    version: 'Version',
    [processRunErrorCodes.RuntimeNotAvailable]: 'worker not available',
    [processRunErrorCodes.StartTimeout]: 'starting timed out',
    [processRunErrorCodes.RunCommandRejected]: 'run command was rejected by the worker',
    [processRunErrorCodes.RuntimeProvisioningError]: 'failed to start container',
    [processRunErrorCodes.WorkerError]: 'failure during execution',
    [processRunErrorCodes.RuntimeRemoved]: 'worker removed',
    [processRunErrorCodes.NoStageBundle]:
      'missing task package file (try re-uploading the file used by this task package)',
    [processRunErrorCodes.ActivityRunTimeout]: 'execution of a step timed out',
    [processRunErrorCodes.ActivityRunStartFailed]: 'failed to start execution of a step',
    [processRunErrorCodes.RuntimeProvisioningFailed]: 'failed to start container',
    [processRunErrorCodes.FailedToReachRuntime]: 'failed to reach worker',
    [processRunErrorCodes.WorkerDidNotCompleteRun]: 'worker did not complete run',
    createTime: 'Create time',
    [activityRunEvents.START]: 'Start',
    [activityRunEvents.ENV_SETUP_START]: 'Worker setup start',
    [activityRunEvents.ENV_SETUP_END]: 'Worker setup end',
    [activityRunEvents.BUNDLE_DL_START]: 'Task package file download start',
    [activityRunEvents.BUNDLE_DL_END]: 'Task package file download end',
    [activityRunEvents.BUNDLE_UNPACK_START]: 'Task package file unpack start',
    [activityRunEvents.BUNDLE_UNPACK_END]: 'Task package file unpack end',
    [activityRunEvents.BUNDLE_INFO]: 'Task package file info',
    [activityRunEvents.EXECUTE_START]: 'Execute start',
    [activityRunEvents.EXECUTE_END]: 'Execute end',
    [activityRunEvents.ARTIFACT_UL_START]: 'Artifact upload start',
    [activityRunEvents.ARTIFACT_UL_END]: 'Artifact upload end',
    [activityRunEvents.END]: 'End',
    runtimeGuide: 'Follow the guide to link your first worker',
    runtimeGroupsGuide: 'Follow the guide to create your first worker group',
    runtimeOnDemand: 'Follow the guide to create your first on demand worker',
    tokenInfo:
      'Generated token is valid for one hour and one activation. You cannot re-use the same or old token to activate worker.',
    token: 'Link Token',
    generatedToken: 'One-time link token',
    [DefaultApiError.GET]: 'Failed to fetch data',
    [DefaultApiError.POST]: 'Failed to save data',
    [DefaultApiError.PUT]: 'Failed to update data',
    [DefaultApiError.PATCH]: 'Failed to update data',
    [DefaultApiError.DELETE]: 'Failed to delete data',
    [DefaultApiError.UNKNOWN]: 'Failed to perform action',
    [WorkspacesQueryErrors.GET_WORKSPACES_ERROR]: 'Error while getting workspaces',
    [WorkspacesQueryErrors.CREATE_WORKSPACE_ERROR]: 'Error while creating workspace',
    [WorkspacesQueryErrors.DELETE_WORKSPACE_ERROR]: 'Error while deleting workspace',
    [WorkspacesQueryErrors.EDIT_WORKSPACE_ERROR]: 'Error while editing workspace',
    [WorkspaceInvitesQueryErrors.GET_WORKSPACE_INVITE_ERROR]:
      'Error while getting a workspace invite',
    [WorkspaceInvitesQueryErrors.ACCEPT_WORKSPACE_INVITE_ERROR]:
      'Error while accepting a workspace invite',
    [WorkspaceMembersQueryErrors.GET_WORKSPACE_MEMBERS_ERROR]:
      'Error while getting workspace members',
    [WorkspaceMembersQueryErrors.REMOVE_WORKSPACE_MEMBER_ERROR]:
      'Error while removing a workspace member',
    [WorkspaceInvitesQueryErrors.DELETE_WORKSPACE_INVITE_ERROR]:
      'Error while deleting a workspace invite',
    [WorkspaceMembersQueryErrors.SET_WORKSPACE_ROLE_ERROR]: 'Could not set workspace role',
    [OrganizatioinsQueryErrors.GET_ORGANIZATIONS_ERROR]: 'Error while getting organizations',
    [OrganizatioinsQueryErrors.CREATE_ORGANIZATION_ERROR]: 'Error while creating organization',
    [OrganizatioinsQueryErrors.DELETE_ORGANIZATION_ERROR]: 'Error while deleting organization',
    [OrganizationInvitesErrors.GET_ORGANIZATION_INVITE_ERROR]:
      'Error while getting a organization invite',
    [OrganizationInvitesErrors.GET_USER_ORGANIZATION_INVITES_ERROR]:
      'Error while getting user invites',
    [OrganizationInvitesErrors.ACCEPT_ORGANIZATION_INVITE_ERROR]:
      'Error while accepting a organization invite',
    [OrganizatioinsQueryErrors.EDIT_ORGANIZATION_ERROR]: 'Error while editing organization',
    [OrganizationInvitesErrors.INVITE_USER_TO_ORGANIZATION_ERROR]:
      'Error while inviting a user to organization',
    [OrganizationMembersErrors.GET_ORGANIZATION_MEMBERS_ERROR]:
      'Error while getting organization members',
    [OrganizationMembersErrors.REMOVE_ORGANIZATION_MEMBER_ERROR]:
      'Error while removing a organization member',
    [OrganizationInvitesErrors.DELETE_ORGANIZATION_INVITE_ERROR]:
      'Error while deleting a organization invite',
    [OrganizationInvitesErrors.SET_ORGANIZATION_INVITE_ROLE_ERROR]:
      'Could not set role for organization invite',
    [UserActionType.GET_CURRENT_USER_ERROR]: 'Error while getting user details',
    [UserActionType.EDIT_CURRENT_USER_ERROR]: 'Error while editing user details',
    [UserActionType.UPDATE_CURRENT_USER_DETAILS_ERROR]: 'Error while updating user details',
    [ProcessActionType.GET_PROCESSES_ERROR]: 'Error while getting processes',
    [ProcessActionType.GET_PROCESS_ERROR]: 'Error while getting process',
    [ProcessActionType.EDIT_PROCESS_ERROR]: 'Error while editing process',
    [ProcessActionType.CREATE_PROCESS_ERROR]: 'Error while creating process',
    [ProcessActionType.DELETE_PROCESS_ERROR]: 'Error while deleting process',
    [ProcessActionType.DELETE_PROCESS_SECRET_ERROR]: 'Error while deleting Process API access',
    [ProcessActionType.GENERATE_PROCESS_SECRET_ERROR]: 'Error while generating Process API access',
    [ActivityActionType.GET_ACTIVITIES_ERROR]: 'Error while getting activities',
    [ActivityActionType.GET_ACTIVITY_ERROR]: 'Error while getting step',
    [ActivityActionType.CREATE_ACTIVITY_ERROR]: 'Error while creating step',
    [ActivityActionType.DELETE_ACTIVITY_ERROR]: 'Error while deleting step',
    [ActivityActionType.EDIT_ACTIVITY_ERROR]: 'Error while editing step',
    [ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES_ERROR]: 'Error while getting robots',
    [ActivityTemplateActionType.GET_ACTIVITY_TEMPLATE_ERROR]: 'Error while getting robots',
    [ActivityTemplateActionType.EDIT_ACTIVITY_TEMPLATE_ERROR]: 'Error while editing a robot',
    [ActivityTemplateActionType.DELETE_ACTIVITY_TEMPLATE_ERROR]: 'Error while deleting a robot',
    [ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE_ERROR]: 'Error while creating a robot',
    [ProcessScheduleActionType.GET_PROCESS_SCHEDULES_ERROR]:
      'Error while getting process schedules',
    [ProcessScheduleActionType.CREATE_PROCESS_SCHEDULE_ERROR]:
      'Error while creating a process schedule',
    [ProcessScheduleActionType.DELETE_PROCESS_SCHEDULE_ERROR]:
      'Error while deleting a process schedule',
    [ProcessScheduleActionType.EDIT_PROCESS_SCHEDULE_ERROR]:
      'Error while editing a process schedule',
    [ProcessRunsErrors.GET_WORKSPACE_PROCESS_RUNS_ERROR]:
      'Error while getting workspace process runs',
    [ProcessRunsErrors.GET_PROCESS_RUNS_BY_PROCESS_ERROR]: 'Error while getting process runs',
    [ProcessRunsErrors.GET_PROCESS_RUN_ERROR]: 'Error while getting a process run',
    [ProcessRunsErrors.STOP_PROCESS_RUN_ERROR]: 'Error while stopping a process run',
    [ProcessRunsErrors.STOP_PROCESS_RUNS_ERROR]: 'Error while stopping process runs',
    [ProcessRunsErrors.COMPLETE_PROCESS_RUNS_ERROR]: 'Error while completing process runs',
    [ProcessRunsErrors.DELETE_PROCESS_RUN_ERROR]: 'Error while deleting process run',
    [ProcessRunsErrors.DELETE_PROCESS_RUNS_ERROR]: 'Error while deleting process runs',
    [ProcessRunsErrors.START_PROCESS_RUN_ERROR]: 'Error while starting process run',
    [WorkItemsQueryErrors.GET_WORK_ITEM_ERROR]: 'Error while getting work item',
    [WorkItemsQueryErrors.GET_WORK_ITEMS_ERROR]: 'Error while getting work items',
    [WorkItemsQueryErrors.DISCARD_WORK_ITEM_ERROR]: 'Error while discarding work item',
    [WorkItemsQueryErrors.CREATE_WORK_ITEM_ERROR]: 'Error while creating work item',
    [WorkItemsQueryErrors.UPDATE_WORK_ITEM_DATA_ERROR]: 'Error while updating work item data',
    [WorkItemsQueryErrors.DELETE_WORK_ITEM_DATA_ERROR]: 'Error while deleting work item data',
    [WorkItemsQueryErrors.RETRY_WORK_ITEMS_ERROR]: 'Error while retrying work items',
    [WorkItemsQueryErrors.GET_WORK_ITEM_DOWNLOAD_LINK_ERROR]:
      'Error while getting work item download link',
    [WorkItemsQueryErrors.GET_WORK_ITEM_UPLOAD_LINK_ERROR]:
      'Error while getting work item upload link',
    [WorkItemsQueryErrors.UPLOAD_WORK_ITEM_FILE_ERROR]: 'Error while uploading work item file',
    [WorkItemsQueryErrors.UPLOAD_NEW_WORK_ITEM_FILE_ERROR]:
      'File upload failed, the created work item will not be run and is available in Data Explorer',
    [WorkItemsQueryErrors.DELETE_WORK_ITEM_FILE_ERROR]: 'Error while deleting work item file',
    [WorkItemSummaryErrors.GET_WORK_ITEM_SUMMARY_ERROR]: 'Error while getting process summary data',
    [ActivityRunActionType.GET_ACTIVITY_RUNS_ERROR]: 'Error while getting robot runs',
    [ActivityRunActionType.GET_ACTIVITY_RUN_ERROR]: 'Error while getting robot run',
    [ActivityRunActionType.GET_ACTIVITY_RUN_CONSOLE_ERROR]: 'Error while getting console messages',
    [ActivityRunActionType.GET_ACTIVITY_RUN_EVENTS_ERROR]: 'Error while getting activityRunEvents',
    [ArtifactActionType.GET_ARTIFACT_LINK_ERROR]: 'Error while getting artifact',
    [ArtifactActionType.LIST_ARTIFACTS_ERROR]: 'Error while getting artifacts',
    [UserActionType.DELETE_CURRENT_USER_ERROR]: 'Error while deleting user account',
    [WorkspacesQueryErrors.DELETE_WORKSPACE_DATA_ERROR]: 'Error while deleting workspace data',
    [UserActionType.DELETE_PERSONAL_WORKSPACE_DATA_ERROR]:
      'Error while deleting personal workspace data',
    [ContainerImageActionType.GET_CONTAINER_IMAGES_ERROR]: 'Error while getting images',
    [ApiKeyActionType.LIST_PERSONAL_API_KEYS_ERROR]: 'Error while getting access credentials',
    [ApiKeyActionType.CREATE_PERSONAL_API_KEY_ERROR]: 'Error while creating access credential',
    [ApiKeyActionType.DELETE_PERSONAL_API_KEY_ERROR]: 'Error while deleting access credential',
    [ApiKeyActionType.EDIT_PERSONAL_API_KEY_ERROR]: 'Error while editing access credential',
    [ApiKeyActionType.GET_PERSONAL_API_KEY_ERROR]: 'Error while loading access credential details',
    [SecretActionType.GET_SECRET_ERROR]: 'Error while getting secret',
    [SecretActionType.GET_SECRET_LIST_ITEMS_ERROR]: 'Error while getting secrets',
    [SecretActionType.DELETE_SECRET_ERROR]: 'Error while deleting secret',
    [SecretActionType.PUT_SECRET_ERROR]: 'Error while creating or updating secret',
    [UserActionType.REQUEST_SLACK_INVITE_ERROR]: 'Error while requesting Slack invite',
    [FeedbackActionType.SEND_FEEDBACK_ERROR]: 'Error while sending feedback',
    [ServiceDeskActionType.SEND_SERVICE_TICKET_ERROR]: 'Error while sending ticket',
    [BillingActionType.CHANGE_SUBSCRIPTION_TIER_ERROR]: 'Error while making subscription',
    [BillingActionType.GET_CURRENT_PAYMENT_METHOD_ERROR]: 'Error while getting payment method',
    [BillingActionType.CREATE_PAYMENT_METHOD_ERROR]: 'Error while creating payment method',
    [BillingActionType.DELETE_PAYMENT_METHOD_ERROR]: 'Error while deleting payment method',
    [BillingActionType.GET_CURRENT_SUBSCRIPTION_ERROR]: 'Error while getting current subscription',
    [BillingActionType.UPDATE_BILLING_INFORMATION_ERROR]:
      'Error while updating billing information',
    [BillingActionType.GET_PLANS_ERROR]: 'Error while getting plans',
    [BillingActionType.LIST_INVOICES_ERROR]: 'Error while listing invoices',
    [BillingActionType.GET_USAGE_ERROR]: 'Error while getting usage',
    [BillingActionType.UPDATE_USAGE_LIMIT_ERROR]: 'Error while updating minute limit',
    [WorkspaceKeysActionType.DELETE_WORKSPACE_KEYS_ERROR]: 'Error while deleting API keys',
    [WorkspaceKeysActionType.DELETE_WORKSPACE_KEY_ERROR]: 'Error while deleting API key',
    [WorkspaceKeysActionType.GET_WORKSPACE_KEYS_ERROR]: 'Error while getting API keys',
    [WorkspaceKeysActionType.GET_WORKSPACE_KEY_ERROR]: 'Error while getting API key',
    [WorkspaceKeysActionType.CREATE_WORKSPACE_KEY_ERROR]: 'Error while creating API key',
    [WorkspaceKeysActionType.EDIT_WORKSPACE_KEY_ERROR]: 'Error while editing API key',
    [LinkGitlabSourceMutationErrors.SET_GITLAB_REPO_ERROR]:
      'Error connecting task package to GitLab',
    [LinkGithubSourceMutationErrors.SET_GITHUB_REPO_ERROR]:
      'Error connecting task package to Github',
    [RobotsQueryErrors.LIST_ROBOTS_ERROR]: 'Failed to fetch list of task packages',
    [MFA_CONFIGURATION_ERROR]: 'MFA configuration issue: reach out to our support',
    triggerSource: 'Started by',
    byAgreeing: 'By using Control Room you accept the',
    maxDurSeconds: 'Max duration (s)',
    deleteProcessRun: 'Delete process run',
    deleteProcessRunText: 'Are you sure you want to delete this process run?',
    deletingProcessRun: 'Deleting run',
    userPicUrl: 'User picture URL',
    password: 'Password',
    passwordHint: 'Manage password at ',
    mfa: 'Multi-Factor-Authentication (MFA)',
    enableMfa: 'Enable MFA for your account',
    disableMfa: 'Disable MFA for your account',
    enableMfaConfirmation: {
      disclaimer: 'You are about to enable MFA for your user account.',
      stepsTitle: 'The following will happen once you click confirm:',
      stepOne: 'You will be logged out',
      stepTwo: 'You will need to login and setup MFA',
      stepThree: 'You will be logged in and MFA will be enabled for your user account',
    },
    disableMfaConfirmation: {
      disclaimer: 'You are about to disable MFA for your user account.',
      stepsTitle: 'The following will happen once you click confirm:',
      stepOne: 'You will be logged out',
      stepTwo: 'You will need to login and enter the MFA code one last time',
      stepThree: 'You will be logged in and MFA will be disabled for your user account',
    },
    notifications: 'Notifications',
    notificationSettings: 'Notification settings',
    notificationSettingsFailedRuns: 'Notifications of failed runs',
    notificationSettingsFailedRunsHelper:
      'Email addresses for sending notifications of failed runs (separated by commas)',
    showTimestamps: 'Show timestamps',
    websocketDisconnectionInfo: 'Live notifications channel is offline, reconnecting...',
    websocketReconnected: 'Live notifications channel is back online',
    updateAvailable: 'There is a new version of Control Room available.',
    updateAvailableTitle: 'Update available',
    updateAvailableForced:
      'There is a new version of Control Room available that requires a page refresh.',
    refreshPage: 'Refresh page',
    clickHere: 'Click here',
    terminateRun: 'Terminate run',
    terminateRuns: 'Terminate {{count}} run',
    terminateRuns_plural: 'Terminate {{count}} runs',
    hardConfirmExplain:
      'Type the following text to confirm action: <br/> <strong>{{ confirmPhrase }}</strong>',
    dangerZone: 'Danger zone',
    deletePrivateWorkspaceData: 'Delete private workspace data',
    deletePrivateWorkspaceDataText:
      'Are you sure you want to delete all content of your private workspace? This action cannot be reverted.',
    deletePrivateWorkspaceDataText2:
      'Delete content of your private workspace. This action cannot be reverted.',
    standardRuntimes: 'Self-hosted',
    standardRuntimesDesc: 'Run Robocorp App on your local machine',
    containerRuntimes: 'Robocorp-hosted',
    robocontainer: 'Cloud Container',
    robocontainerText: 'Fully managed cloud worker offered by Robocorp',
    mb: 'MB',
    kb: 'KB',
    selectImage: 'Select image',
    runSocketConnected: 'Run live data channel connected',
    runSocketDisconnected: 'Run live data channel disconnected. Reconnecting...',
    processes: 'Processes',
    processesDesc: 'A process is a combination of steps that implement a business process.',
    processesEmptyDescription:
      'Process is a combination of steps. Each step is performed by a task, that automatically handles the work items according to your instructions.',
    createProcessNameDescription:
      'Process name is good to be unique and describe the action that is automated.',
    processEmptyDescription:
      'A process is a combination of steps that implement a business process. In each step, a task is run one or multiple times.',
    newProcess: 'New Process',
    createProcess: 'Create Process',
    steps: 'Steps',
    step: 'Step',
    stepCount: '{{count}} step',
    stepCount_plural: '{{count}} steps',
    noStepsDefined: 'No steps defined',
    noStepsDefinedDescription: 'TODO',
    moveUp: 'Move up',
    inSeconds: 'in {{count}} second',
    inSeconds_plural: 'in {{count}} seconds',
    manageRobots: 'Manage available task packages <0>on the task page</0>.',
    noStandardRuntimes: 'No self-hosted workers available.',
    manageRuntimes: 'Manage available workers on the',
    runtimesPage: 'workers page',
    newActivity: 'New Step',
    createActivity: 'Create Step',
    addToProcess: 'Add to process',
    deleteProcess: 'Delete Process',
    deleteProcessText: 'Are you sure you want to delete this process?',
    deletingProcess: 'Deleting process',
    activityByIndex: 'Step {{index}}',
    activityUntitled: 'Untitled Step',
    activityRun: 'Step run',
    activityRuns: 'Step runs',
    activityRunDetails: 'Step run details',
    processRunDetails: 'Process run details',
    deleteActivity: 'Delete step',
    deleteActivityText: 'Are you sure you want to delete this step?',
    priority: 'Priority',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    activityPriorityText:
      'Priority specifies the order of execution for step runs that are queueing to the same worker.',
    emailNotInWorkspace: 'External member',
    deletingActivity: 'Deleting step',
    deletingActivityTemplate: 'Deleting robot',
    noActivityTemplates: 'No task packages',
    processRunActivityRuns: 'Step runs',
    concurrencyLimit: 'Parallel executions',
    concurrencyLimitHelper: 'Maximum number of parallel executions',
    stepConfiguration: {
      stepNameDescription: 'Give the step short and clear name.',
      useRDPConnection: 'Use Desktop Connection',
      useRDPConnectionDesc:
        'Use Remote Desktop Protocol (RDP) with predefined credentials to run the step. Supported for Windows worker only.',
      sessionConfigurationDivider: 'Session configuration',
      desktopLoginTimeout: 'Maximum duration (in seconds) for desktop login',
      desktopLoginTimeoutDesc:
        'The connection will be closed if the user has not logged in by that time.',
      desktopNoSignoutAfterRun: 'Do not sign out after each step run',
      desktopNoSignoutAfterRunDesc:
        'Leaves user session and applications open after a step run. Use this when the task benefits from re-using previous session. Supported since Workforce Agent Core v6.23.0',
      videoStreamDivider: 'Video streaming',
      videoStreamingEnabled: 'Enable Video Streaming',
      videoStreamingEnabledDesc: `Capture a screen recording of this step. This allows you to either monitor the execution as it's happening, or watch it at a later time.`,
      toggleAdvanced: 'Toggle advanced settings',
      execTimeout: 'Execution timeout (s)',
      execTimeoutHelper: 'Execution max duration (s)',
      conditionalStart: {
        label: 'Start only after',
        allPreviousWorkItemsProcessed:
          'All work items from previous steps are either done or failed.',
      },
    },
    processRunStartSuccess: 'Process Run started succesfully',
    processRuns: 'Process runs',
    processRunNo: 'Process run #{{runNo}}',
    workItemState: {
      [WorkItemState.New]: 'New',
      [WorkItemState.Pending]: 'Pending',
      [WorkItemState.Registered]: 'Registered',
      [WorkItemState.InProgress]: 'In Progress',
      [WorkItemState.Failed]: 'Failed',
      [WorkItemState.Completed]: 'Done',
      [WorkItemState.Output]: 'Output',
    },
    processRunState: {
      [ProcessRunState.New]: 'New',
      [ActivityRunState.AwaitingRuntime]: 'Awaiting Worker',
      [ProcessRunState.InProgress]: 'In Progress',
      [ProcessRunState.Pending]: 'Unresolved',
      [ProcessRunState.Stopping]: 'Stopping',
      [ProcessRunState.Complete]: 'Completed',
    },
    allRuns: 'All Process Runs',
    processRunResult: {
      [ProcessRunResult.Ok]: 'Completed',
      [ProcessRunResult.Error]: 'Error',
      [ProcessRunResult.Timeout]: 'Timeout',
      [ProcessRunResult.Terminated]: 'Terminated',
      [ProcessRunResult.ConnectionLoss]: 'Connection Loss',
      [ProcessRunResult.StartFailed]: 'Failed to Start',
    },
    workItemExceptionType: {
      [FailedWorkItemExceptionType.Application]: 'Application',
      [FailedWorkItemExceptionType.Business]: 'Business',
      [FailedWorkItemExceptionType.Unspecified]: 'Unspecified',
      [FailedWorkItemExceptionType.Orchestrator]: 'Orchestrator',
    },
    stepRunState: {
      [ActivityRunState.New]: 'New',
      [ActivityRunState.AwaitingRuntime]: 'Awaiting Worker',
      [ActivityRunState.InProgress]: 'In Progress',
      [ActivityRunState.Complete]: 'Completed',
    },
    stepRunResult: {
      [ActivityRunResult.ConnectionLoss]: 'Connection Loss',
      [ActivityRunResult.Error]: 'Failed',
      [ActivityRunResult.Ok]: 'Completed',
      [ActivityRunResult.StartFailed]: 'Failed to Start',
      [ActivityRunResult.Terminated]: 'Terminated',
      [ActivityRunResult.Timeout]: 'Timeout',
    },
    assistantRunState: {
      [AssistantRunFullState.InProgress]: 'In Progress',
      [AssistantRunFullState.Complete]: 'Completed',
    },
    assistantRunResult: {
      [AssistantRunFullResult.Ok]: 'Completed',
      [AssistantRunFullResult.Error]: 'Failed',
      [AssistantRunFullResult.Terminated]: 'Terminated',
      [AssistantRunFullResult.Timeout]: 'Timeout',
    },
    exceptions: 'Exceptions',
    noExceptions: 'No exceptions',
    exception: 'Exception',
    workQueue: 'Input queue',
    key: 'Key',
    accessCredential: 'Access Credential',
    apiKeys: 'Access Credentials',
    addAccessCredentials: 'New Access Credential',
    createAccessCredentials: 'Create Access Credential',
    noAccessCredentials: 'No Access Credentials',
    editAccessCredentials: 'Edit Access Credential',
    addApiKey: 'New API Key',
    deleteApiKey: 'Delete Credential',
    deleteApiKeyText:
      'Are you sure you want to delete this access credential? This action cannot be reverted.',
    noUses: 'No uses',
    view: 'View',
    preview: 'Preview',
    copyInvitationLink: 'copy invitation link',
    description: 'Description',
    noDescription: 'No description',
    close: 'Close',
    hide: 'Hide',
    personalApiKeysWarning:
      'Access credentials are tied to your user account and grant the same privileges as your user account',
    useCount: 'Uses',
    vaultDesc:
      'Vault consists of a collection of secrets that can be used to provide sensitive information, such as credentials, to the worker.',
    createSecret: 'Create Secret',
    items: 'Items',
    addItem: 'Add Item',
    secretNameHelper: 'Provide an unique name without whitespace or special characters',
    value: 'Value',
    uniqueKeyHelper: 'Keys must be unique',
    fromUrl: 'From URL',
    importUrlInstructions: 'Import a direct link to a .zip file',
    cmdOnly: 'Command only',
    cmdOnlyDescription: 'Run execution command only',
    execution: 'Execution',
    envVars: 'Environment Variables',
    andMore: 'and {{count}} more',
    plusMore: '+ {{count}} more',
    noEnvVarsDefined: 'No variables defined',
    addVariable: 'Add Variable',
    activityVariableDesc:
      'These environment variables are only set during the executions of this robot.',
    updateTime: 'Update time',
    [ErrorSubCodes.ERR_ACTIVITY_ENV_MISSING]: 'Worker missing from step',
    [ErrorSubCodes.ERR_ACTIVITY_RUN_COMMAND_MISSING]: 'Run command missing from task package',
    [ErrorSubCodes.ERR_NO_ACTIVITIES]: 'Process has no steps configured',
    [ErrorSubCodes.GIT_REFERENCE_NOT_FOUND_ERROR]:
      'The git reference specified as pinned version could not be found for this repository',
    [ErrorSubCodes.TASK_PACKAGE_NOT_FOUND_ERROR]: 'The task package cannot be found',
    [ErrorSubCodes.ERR_TEMPLATE_IN_USE]:
      'This task package is used by a process and cannot be deleted',
    [ErrorSubCodes.ROBOT_WITH_SAME_NAME_ALREADY_EXISTS]:
      'A task package with this name already exists',
    [ErrorSubCodes.ERR_WORKSPACE_DISABLED]:
      'Workspace is disabled. Please contact support to resolve the issue.',
    [ErrorSubCodes.EMAIL_MISMATCH]: 'Email mismatch in adding user',
    [ErrorSubCodes.TOO_MANY_WORKSPACES]: 'You cannot create any more workspaces',
    [ErrorSubCodes.TOO_MANY_PROCESSES]:
      'Process limit reached. Please contact support to increase the limit.',
    [ErrorSubCodes.TOO_MANY_ACTIVITIES]:
      'Step limit reached. Please contact support to increase the limit.',
    [ErrorSubCodes.TOO_MANY_USERS_INVITED]: 'User invite limit reached. Please contact support.',
    [ErrorSubCodes.TOO_MANY_USERS_IN_WORKSPACE]: 'You cannot have more users in this workspace',
    [ErrorSubCodes.TOO_MANY_ACCESS_CREDENTIALS]: 'Access Credential limit reached',
    [ErrorSubCodes.TOO_MANY_ACTIVITY_TEMPLATES]:
      'Task package limit reached. Please contact support to increase the limit.',
    [ErrorSubCodes.TOO_MANY_WORKERS_IN_WORKSPACE]:
      'Worker limit reached. Please contact support to increase the limit.',
    [ErrorSubCodes.ERR_ORG_INSERT_FAILED]: 'There was an issue in creating the organization',
    [ErrorSubCodes.RESOURCE_IN_USE]: 'Task is used by a process',
    [ErrorSubCodes.USER_NOT_IN_ORG]: 'User is not member of organization',
    [ErrorSubCodes.USER_ALREADY_IN_WS]: 'User is already member of workspace',
    [ErrorSubCodes.GIT_CLONE_FAILED]: 'Failed to clone git repository',
    [ErrorSubCodes.TOO_MANY_ACTIVE_ASSISTANTS]:
      'Active assistants limit reached. You can purchase more assistant seats from billing',
    [ErrorSubCodes.UNAUTHORIZED_TO_LOAD_WORK_ITEMS]:
      "You're unauthorized to see and edit work items",
    [ErrorSubCodes.ENDPOINT_VALIDATION_FAILED]: 'Endpoint URL is not a valid public address',
    [ErrorSubCodes.ERR_CANNOT_REMOVE_LAST_ENV_FROM_ACTIVE_GRP]:
      'Cannot remove the last worker from the group while the group is used by a process. Unlink the process first',
    [ErrorSubCodes.ERR_RUNTIME_IN_USE]: 'This worker is used in at least one process',
    [ErrorSubCodes.ERR_ENVIRONMENT_GROUP_IN_USE]:
      'This worker group is used in at least one process',
    workItems: 'Work items',
    chooseWorkItem: 'Choose Work item',
    chooseWorkItemFile: 'Choose Work item file',
    noFailedWorkItems: 'No failed work items',
    failedWorkItems: 'Failed work items',
    workItemSuccessRate: 'Work Item Success Rate',
    workItemSuccessRateTooltip:
      '{{successRate}}% – {{succeededItems}} work items done /\n {{totalItems}} work items processed in total',
    noRetryWorkItemsForProcess: 'No failed work items',
    noPendingWorkItemsForProcess: 'No pending work items',
    noWorkItemsWithFiles: 'No work items with files',
    acceptInvite: 'Accept Invite',
    invitesTitle: 'Pending invitations',
    invitesDescription: 'You have a pending invitation to an organization',
    invitesDescription_plural: 'You have pending invitations to {{count}} organizations',
    pendingOrganizationInvitations: 'Pending organization invitations',
    downloadRobocode: 'Download Robocorp Lab',
    fromPackage: 'From robot file',
    workerInstaller: 'Installers',
    availableActivities: 'Available tasks',
    tasks: 'Tasks',
    dockerImage: 'Docker Image',
    numberOfActivitiesInTemplate: 'Number of tasks',
    activityTemplateName: 'Robot name',
    activityTemplateSource: 'Origin',
    activityTemplateIdShort: 'Robot ID',
    activityTemplateShort: 'Robot',
    activityTemplatesShort: 'Robots',
    activityTemplate: 'Robot',
    activityTemplates: 'Robots',
    activityTemplatePackage: 'Robot',
    activityTemplateGitHubInstructions: ' You can edit the permissions at',
    activityTemplateGitHubAccount: 'GitHub Control Room app settings',
    activityTemplateGitHubConnect: 'Connect GitHub Account',
    activityTemplateGitLabAccount: 'GitLab Control Room app settings',
    linkGithubWithControlRoom: 'Link GitHub app with your Control Room tasks',
    connectGitLabAccount: 'Connect GitLab Account',
    activityTemplateBranchInstructions:
      'The default branch will be deployed if the field is left blank or if the branch does not exist',
    activityTemplateGitHubConnectInstructions:
      'Use task packages from private repositories by installing the Robocorp Control Room app to GitHub.',
    activityTemplateGitLabConnectInstructions:
      'Use task packages from private repositories by installing the Robocorp Control Room app to GitLab.',
    addStep: 'Add step',
    addActivityToProcess: 'Add step to a process',
    noActivityTemplatePackageFound: 'No robot found',
    selectActivityTemplate: 'Select a task',
    activityId: 'Step ID',
    activityNotAvailableInPackage: 'Robot not available in the robot file.',
    editActivityTemplate: 'Edit task package',
    saveActivityTemplate: 'Save task package',
    editOnline: 'Edit online',
    activityEditSuccessfully: 'Task package updated succesfully',
    url: 'URL',
    or: 'OR',
    download: 'Download',
    createActivityTemplate: 'Create Task Package',
    createNewRobot: 'Add New Task Package...',
    robotProcessed: 'Task package is processed...',
    removeActivityFromProcess: 'Remove step from a process',
    removingActivity: 'Removing step',
    removeActivityFromProcessText: 'Are you sure you want to remove this step?',
    robocodeAssist: 'Robocorp CLI Assist',
    uploadPackage: 'Upload robot file',
    downloadPackage: 'Download robot file',
    editSource: 'Edit Origin',
    apply: 'Apply',
    overwrite: 'Overwrite',
    noPackageFound: 'No robot file found',
    installationGuide: 'Installation Guide',
    viewEulaPdf: 'Show EULA (PDF)',
    robocodeDescription:
      'Robocorp Lab is an IDE for creating software robots. It includes everything you need to get started. Robocorp Lab integrates with the Control Room, making it easy to iterate your software robot development process.',
    noSourceSet: 'No origin set',
    noSourceSetHelper: 'Provide an origin to use this robot in a process',
    creating: 'Creating',
    noInvitesAdded: 'No invites added',
    noActivityRuns: 'No step runs',
    noProcessRuns: 'No process runs',
    noWorkItemsProcessed: 'No work items processed yet',
    noRunsProcessed: 'No runs yet',
    trackedSince: 'Tracked since',
    noWorkItems: 'No work items',
    noActivities: 'No steps',
    noArtifacts: 'No artifacts',
    noSecretsAdded: 'No secrets added',
    noTasks: 'No tasks',
    loading: 'Loading',
    loadingControlRoom: 'Loading Control Room',
    noEvents: 'No events',
    noFailureEmailsAdded: 'No failure notification email recipients',
    noSuccessEmailsAdded: 'No success notification email recipients',
    startedRuns: 'Started runs',
    unresolvedRuns: 'Unresolved runs',
    completedRuns: 'Completed runs',
    noProcesses: 'No processes',
    noSchedulesAdded: 'No schedule added',
    moreOptions: 'More Options',
    and: 'and',
    termsOfUse: 'Terms of Use',
    ourTermsOfUse: 'terms of use',
    privacyPolicy: 'Privacy policy',
    noActivitiesConfigured: 'No robots configured',
    cannotAddActivitiesToProcess: 'You can only have {{limit}} steps per process',
    cannotAddProcesses: 'You can only have {{limit}} processes in this workspace',
    cannotAddSecrets: 'You can only have {{limit}} secrets in this workspace',
    cannotCreateActivities: 'You can only create {{limit}} robots in this workspace',
    cannotCreateApiKeys: 'You can only create {{limit}} access credentials in this workspace',
    cannotCreateWorkspace: 'This organization is limited to {{limit}} workspaces',
    cannotInvite: 'You can only invite {{limit}} users',
    cannotHaveMoreUsers: 'This organization is limited to {{limit}} users',
    cannotCreateMoreOrganizations: 'You can only create {{limit}} organizations',
    cannotAddSteps: 'You are not permitted to add new steps',
    joinCommunity: 'Join our community on Slack',
    joinCommunityDesc:
      'Get announcements and community support. Hang out with software developers from all over the world.',
    sendInvite: 'Send invite',
    inviteSent: 'There should be an invite in your email!',
    openSlack: 'Open Slack',
    cannotNotifySlack: 'Cannot notify Slack',
    contactSupport: 'Contact support',
    contactUsAt: 'Contact us at',
    errorProcessingRequest:
      'There was an issue processing the request. If the problem persists, contact us at',
    alreadyJoined:
      'Looks like you have already received an invite. If you are having trouble, contact support.',
    sendFeedbackTitle: 'Give Feedback',
    sendFeedbackSubtitle: 'Feedback',
    feedbackPlaceholder:
      "Request features, report issues or anything else you'd like to share with us!",
    featureRequest: 'Request a feature',
    reportIssue: 'Report an issue',
    sendQuestion: 'Send us a question',
    npsQuestion: 'How likely are you to recommend Robocorp to a friend?',
    npsLikely: 'Very likely',
    selfHostedGitlabDialogTitle: 'Install self-managed gitlab installation',
    selfHostedGitlabRedirectURICopy: 'Copy redirect URI',
    selfHostedGitlabUrl: 'Gitlab URL',
    selfHostedGitlabDocsLink: 'Link to Docs',
    selfHostedGitlabUrlDescription:
      'The URL of your Gitlab instance. Example: https://gitlab.example.com',
    selfHostedUrl: 'URL',
    selfHostedGitlabClientId: 'Application ID',
    selfHostedGitlabClientIdDescription: 'The Application ID of your Gitlab application',
    selfHostedGitlabClientSecret: 'Application Secret',
    selfHostedGitlabClientSecretDescription: 'The Application Secret of your Gitlab application',
    npsNotLikely: 'Not at all likely',
    feedbackSent: 'Thanks for the feedback!',
    feedbackSentDesc: 'We’ll be in touch if we need more information. Please enjoy Control Room!',
    continue: 'Continue',
    errorSendingFeedback: 'There was an issue sending the feedback. Please try again later.',
    sendFeedback: 'Send feedback',
    errorSendingTicket: 'There was an issue sending the ticket. Please try again later.',
    robocloudDocumentation: 'Control Room Documentation',
    help: 'Help',
    workspaceMemberEmail: 'Workspace member email',
    processSecret: 'Process API access',
    processSecretsAreDeprecated: 'Process Secrets are deprecated. Use Workspace API keys instead',
    processSecretDescription: 'Generate a secret to run the process from other systems.',
    generateSecret: 'Generate secret',
    secretSet: 'Access secret is active',
    secretSetDescription:
      'You can now run the process from other systems. This secret is strictly scoped to a single process. Treat the secret with high security, just like any other access credential.',
    noSecretSet: 'Access secret is inactive',
    deleteProcessSecret: 'Delete Process API access',
    deleteProcessSecretText:
      'Are you sure you want to delete the process API access? All your existing API calls will stop working.',
    regenerate: 'Regenerate',
    regenerateProcessSecret: 'Regenerate Process API access',
    regenerateProcessSecretText:
      'Are you sure you want to generate a new secret? All your existing API calls will stop working.',
    commandWithSecret: 'Example command',
    triggerProcessText: 'Run the process "{{processName}}" with the following command:',
    activeProcessRuns: 'Active runs',
    unresolvedProcessRuns: 'Unresolved runs',
    finishedProcessRuns: 'Completed runs',
    options: 'Options',
    manual: 'Manual',
    allProcesses: 'All processes',
    renameProcess: 'Rename process',
    rcs: 'Control Room',
    disabled: 'Disabled',
    enabled: 'Enabled',
    disable: 'Disable',
    disable_plural: 'Disable {{ count }}',
    enable: 'Enable',
    enable_plural: 'Enable {{ count }}',
    configure: 'Configure',
    workspaceDisabledSnack:
      'The workspace "{{name}}" is disabled. Processes cant be started. Contact support to resolve the issue',
    organizationDisabledSnack:
      'The organization "{{name}}" is disabled. Please contact support to resolve the issue',
    organizationExceededMinutesSnack:
      'The organization "{{name}}" has exceeded its run minute limit and is disabled. Please increase the limit or contact your admin.',
    organizationExceededAssistantRunsSnack:
      'The organization "{{name}}" has exceeded its assistant run limit and is disabled. Please increase the limit or contact your admin.',
    organizationOnInactivePlanSnack:
      'The organization "{{name}}" is on outdated billing plan. Please update your billing plan option.',
    organizationNotPaidSnack:
      'There was an error processing payment for organization "{{name}}". Please check your payment details or contact support',
    outOfRunMins: 'Out of run minutes',
    outOfAssistantRuns: 'Out of assistant runs',
    onInactivePlan: 'On outdated plan',
    runMinutesExceededDesc:
      'The limit is already exceeded, it will take action next month and no extra cloud minutes will be used. Please increase the on-demand minute limit to continue using robots.',
    assistantRunsExceededDesc:
      'The limit is already exceeded, it will take action next month and no extra assistant runs will be used. Please increase the on-demand assistant run limit to continue using this.',
    organizationDisabled: 'Organization is disabled',
    workspaceDeleted: 'Workspace "{{workspaceName}}" deleted!',
    organizationDeleted: 'Organization "{{organizationName}}" deleted!',
    workspaceNotFound: 'Workspace not found',
    workspaceNotFoundDesc:
      'The workspace you tried to access does not exist or you do not have right to access it.',
    workspaceNotFoundInstructions:
      'Please make sure the link is correct and check with the workspace owner that it has been shared with you.',
    selectWorkspace: 'Select workspace',
    [RuntimeType.Container]: 'Robocorp-hosted',
    [RuntimeType.Standard]: 'Self-hosted',
    noWorker: 'No worker specified',
    noCmdSpeficied: 'No command specified',
    number: 'Number',
    workerName: 'Worker name',
    workerType: 'Worker type',
    organizationNotFound: 'Organization not found',
    organizationNotFoundDesc:
      'The organization you tried to access does not exist or you are not a part of it.',
    organizationNotFoundInstructions:
      'Please make sure the link is correct and check with the organization owner that you have been given access.',
    newOrganization: 'New Organization',
    createOrganization: 'Create Organization',
    createNewOrganization: 'Create a new organization',
    createOrganizationDescription:
      'Organizations is the top level worker to setup your operations. You can create multiple workspaces inside the organization for fine grained access.',
    organizationName: 'Organization name',
    organizationNameDesc: 'Each organization has separate management and billing.',
    organizationCreateRedirect:
      'You will be redirected to the new organization page when creation is finished',
    organizationCreateShortName:
      'This is the prefix of your organization URL. You can also change it later. Lower case letters and numbers only, 1-50 character length',
    organization: 'Organization',
    active: 'Active',
    noWorkspaces: 'No workspaces',
    noWorkspacesFound: 'No workspaces found',
    noOrganizations: "You don't have any organizations",
    noMembersInWorkspace: 'No members in workspace',
    organizationSettings: 'Organization settings',
    organizationInvites: 'Organization invites',
    webAddress: 'Web address',
    createOrganizationDescriptionShort: 'You can create and belong to more than one organization.',
    createOrganizationDescriptionLong:
      'Inside your organisation, use workspaces for organizing your processes and for managing user access. ',
    organizationExists: 'An organization with the address "{{address}}" already exists',
    invitePending: 'invite pending',
    organizationMembers: 'Members',
    deleteOrganizationInvite: 'Cancel organization invite',
    deleteOrganizationInviteText: 'Are you sure you want to remove organization invite for',
    loadingOrganizationMembers: 'Loading organization members',
    deleteOrganizationMember: 'Remove organization member',
    deleteOrganizationMemberText: 'Are you sure you want to remove organization member',
    organizationDetails: 'Details',
    billing: 'Billing',
    plans: 'Plans',
    billingInfo: 'Billing info',
    invoices: 'Invoices',
    deleteOrganization: 'Delete organization',
    deleteOrganizationText:
      'Are you sure you want to delete this organization? This will also remove all organization related data. This action cannot be reverted.',
    deleteOrganizationText2:
      'Delete the organization and all data associated with it. This action cannot be reverted.',
    profile: 'Profile',
    account: 'Account',
    overview: 'Overview',
    performance: 'Performance',
    members: 'Members',
    planAndBilling: 'Plan & Billing',
    billingDisabled: 'Self-service billing is disabled.',
    personalInfo: 'Personal info',
    organizationCreated: 'Organization "{{ organizationName }}" created!',
    workspacesInOrganization: 'Organization has workspaces and cannot be deleted',
    processCount: '{{count}} processes',
    processCountSingular: '{{count}} process',
    thanksForSigningUp: 'Thanks for signing up!',
    signUpDetails: "We'll just need a few more details.",
    inviteToSlack: 'Invite me to Robocorp community on Slack',
    sendProductNews: 'Send me product news and occasional updates',
    askForMainRole: 'What is your main role?',
    developer: 'Developer',
    businessUser: 'Business user',
    technicalManager: 'Technical manager',
    businessManager: 'Business manager',
    operationsOrSupport: 'Operations or Support',
    executive: 'Executive',
    other: 'Other',
    changesToTerms: 'We have made changes to our policies. See the updated documents here:',
    agreeToTerms:
      'I agree to the terms of use, privacy policy and adhere to the EU data processing regulations.',
    checkEmail: 'Check your email',
    sentActivationLink: ' We sent you an activation link.',
    activationLinkDesc:
      "If you haven't received the email within a few minutes, please double-check your spam/junk folder.",
    havingTroubleWithSignup: 'Having trouble with sign-up?',
    contactUs: 'Contact us',
    NA: 'NA',
    next: 'Next',
    previous: 'Previous',
    askForBusiness: 'What is your main business?',
    askForTeamCount: 'How many people work in your organization?',
    askForPurpose: 'Who is this organization for?',
    finance: 'Finance',
    retail: 'Retail',
    healthCare: 'Health care',
    industrial: 'Industrial',
    publicServices: 'Public services',
    education: 'Education',
    consulting: 'Consulting',
    informationTechnology: 'Information technology',
    hobbyProjects: 'Hobby projects',
    tellMoreAboutOrg: 'Tell a bit more about your organization.',
    justMe: 'Just me',
    people: 'People',
    moreThan: 'More than {{amount}}',
    purpose: {
      me: 'Me',
      org: 'My team or organization',
      client: 'My client',
    },
    backgroundPrompt: 'Tell us a bit about your background',
    backgroundDetails:
      'This helps us at making the product better and at giving you better product support.',
    askForProgrammingSkills: 'How would you rate your programming skills?',
    askForRpaExperience: 'What is your level of experience with RPA?',
    noSkills: 'No skills',
    highlySkilled: 'Highly skilled',
    skip: 'Skip',
    yourSelectedPlan: 'Your selected plan',
    addPaymentMethodToSubscribe: 'Add a credit card to make a subscription',
    subscribe: 'Subscribe',
    yourSubscription: 'Your subscription',
    month: 'month',
    free: 'Free',
    forFaq: 'For FAQ and full feature list see the',
    seeRobocorpSuite: 'Robocorp Suite pricing page',
    recurringPriceInline: `\${{price}} / mo x`,
    recurringPrice: `\${{price}} <small>/mo</small>`,
    country: 'Country',
    billingInformation: 'Billing information',
    address: 'Street address',
    businessAddress: 'Business Address',
    city: 'City',
    zipCode: 'Zip code',
    billingState: 'State',
    billingEmail: 'Billing email',
    expires: 'Expires',
    loadingBillingInformation: 'Loading billing information',
    billingNameDesc: "Your name or your company's name",
    billingEmailDesc: 'The invoices will be sent to this email',
    addAddressLine2: 'Add address line 2',
    addressLine2: 'Address line 2',
    optional: 'Optional',
    addBillingInfo: 'Add billing info',
    noBillingInfoAdded: 'No billing info added',
    noCreditCardAdded: 'No credit card added',
    paymentInformation: 'Payment information',
    loadingPaymentInformation: 'Loading payment information',
    creditCard: 'Credit card',
    chooseAState: 'Choose a state...',
    cardholdersName: "Cardholder's name",
    remove: 'Remove',
    removeCard: 'Remove card',
    removeCreditCard: 'Remove credit card',
    confirmDelete: 'Are you sure you want to remove this credit card?',
    updatingBillingInformation: 'Updating billing information',
    costManagement: 'Cost management',
    pricingPage: 'Robocorp pricing page',
    loadingCreditCard: 'Loading credit card',
    removingCreditCard: 'Removing credit card',
    currency: {
      usd: '$',
      eur: '€',
    },
    creditCardDetails: 'Credit card details',
    creditCardHolderName: 'Card holder hame',
    creditCardHolderEmail: 'Card holder email',
    creditCardNumber: 'Credit Card Number',
    creditCardNotDefinedYet: 'Credit card not defined yet',
    loadingCreditCardDetails: 'Loading credit card details',
    addCreditCard: 'Add a credit card',
    expiryMonth: 'Expiry Month',
    expiryYear: 'Expiry Year',
    submit: 'Submit',
    cloudRunMinutes: 'step run minutes',
    plan: 'Plan',
    robotAssistantDesc: '*$30 / additional robot, 1 is included',
    robotAssistants: 'Robot Assistants',
    purchaseSubscription: 'Purchase subscription',
    processing: 'Processing',
    editSubscription: 'Edit Subscription',
    updateSubscription: 'Update Subscription',
    assistantSeatsNotAllowed: 'Purchasing assistants is not allowed on this tier',
    currentSubscriptionAssistants: '* {{amount}} assistans in use until ',
    titleCurrentExpenditure: 'Current Expenditure',
    purchasing: 'Purchasing',
    price: 'Price',
    paymentSuccessful: 'Payment successful',
    thanksForSubscribing: 'Thanks for subscribing to Control Room!',
    startUsingPlan: 'Start using your new plan',
    somethingWentWrong: 'Something went wrong!',
    pleaseCheckCard: 'Please check your credit card',
    goBack: 'Go back',
    team: 'Team',
    pro: 'Pro',
    platformFeatures: 'Robot Workforce features',
    organizationFeatures: 'Organization features',
    workspaceFeatures: 'Workspace features',
    userSeats: 'User seats',
    unlimited: 'Unlimited',
    selfHostedWorkers: 'Self-hosted workers',
    cloudWorkers: 'Cloud workers',
    customOrgUrl: 'Custom organization URL',
    customWsUrl: 'Custom workspace URL',
    roleBasedUserManagement: 'Role-based user management',
    creditCardBilling: 'Credit card billing',
    invoiceHistory: 'Invoice history',
    activityPackageManagement: 'Activity Package management',
    workerManagement: 'Worker management',
    processFeatures: 'Process features',
    multipleActivitiesInProcess: 'Multiple activities in a process',
    transactionDataManagement: 'Transaction data management',
    workQueues: 'Work queues',
    activityRunLogs: 'Activity run logs',
    notificationOfFailedRuns: 'Notification of failed runs',
    processRunModes: 'Process run modes',
    supportForWindows: 'Support for Windows',
    supportForMac: 'Support for macOS',
    supportForLinux: 'Support for Linux',
    encryptedConnection: 'Encrypted connection',
    runAsBackgroundService: 'Run as a background service',
    automaticUpdates: 'Automatic updates',
    support: 'Support',
    communitySupport: 'Community support',
    sla: 'SLA',
    showAllFeatures: 'Show all features',
    serviceDesk: 'Service desk',
    frequentlyAskedQuestions: 'Frequently asked questions',
    faqCloudRunMinutes: 'What are robot run minutes, and how are they billed?',
    faqCloudRunMinutes1:
      'Package of running time "robot run minutes" is included in each tier. The package is refreshed at the start of each billing cycle.',
    faqCloudRunMinutes2:
      'Robot run minutes are consumed when you run robots with either self-hosted or Robocorp hosted Workers. Robot run minutes are counted for each beginning minute.',
    faqCloudRunMinutes3:
      'Unused robot run minutes do not transfer to the next subscription period.',
    faqCloudRunMinutes4:
      'Additional robot run minutes can be bought with Team and Pro subscriptions for a cost of $0.05 per minute.',
    faqCloudRunMinutes5:
      'Additional run minutes used are billed at the start of the next billing cycle.',
    faqSubscriptionFee: 'How is the subscription fee billed?',
    faqSubscriptionFee1:
      'The subscription fee is billed at the start of the subscription period and then after each renewal.',
    faqPaymentMethods: 'What payment methods are available?',
    faqPaymentMethods1:
      'We will support credit card payments at first; other payment methods will be added later.',
    faqUpgradeDowngrade: 'Can I upgrade or downgrade the subscription?',
    faqUpgradeDowngrade1:
      'You will be able to upgrade and downgrade subscriptions. Upgrades are applied immediately, in case of downgrades change is applied at the end of the billing cycle.',
    faqManageSubscription: 'How can I manage my subscription?',
    faqManageSubscription1: 'Subscription can be managed from organization billing settings.',
    select: 'Select',
    selectPlan: 'Select plan',
    currentPlan: 'This is your current plan',
    currentlySelectedPlan: 'This is your currently selected plan',
    planIsEffectiveUntil: 'plan is effective until',
    planDowngradeSuccessful: 'Plan downgraded',
    planDowngradeDesc: 'The plan will be downgraded after the billing period is over',
    prorationDesc:
      'The subscription fee will be prorated according to the time left in your billing cycle.',
    upgrade: 'Upgrade',
    upgradeYourPlan: 'Upgrade Your plan',
    mins: 'mins',
    includedMinutes: 'Included minutes',
    minutesUsed: 'minutes used',
    minutesLeft: 'minutes left',
    currentExpenditure: 'Current expenditure',
    maxExpenditure: 'Max expenditure',
    onDemandMinuteLimit: 'On-demand minute limit',
    noFurtherMinsAllowed: 'No further minutes allowed',
    includedAssistantRuns: 'Included assistant runs',
    assistantRunsUsed: 'assistant runs used',
    assistantRunsLeft: 'assistant runs left',
    onDemandAssistantRunLimit: 'On-demand assistant runs limit',
    noFurtherAssistantRunsAllowed: 'No further assistant runs allowed',
    onDemandAssistantRuns: 'On-demand assistant runs',
    assistantUserSeatsNotAvailableP1: `Assistant seat pricing is not active yet.`,
    assistantUserSeatsNotAvailableP2: `Assistant use won't be charged for the time being.`,
    assistantUserSeats: `Assistant user seats`,
    onDemand: 'On demand',
    totalCost: 'Total cost',
    selfHosted: 'Self-hosted',
    selfHostedMinutes: 'Self-hosted minutes',
    cloudMinutes: 'Cloud minutes',
    includedInMonthlyPlan: 'Included in the {{currency}}{{planPrice}} monthly plan',
    onDemandActivityRunMinutes: 'On-demand task run minutes',
    onDemandMinutes: 'On-demand minutes',
    allowOnDemandRunMinutes: 'Allow on-demand task run minutes',
    onDemandRunMinuteLimit: 'On-demand run minute limit',
    costEstimate: 'Cost estimate',
    pleaseFillField: 'Please fill in this field',
    min: 'min',
    rate: 'rate',
    basic: 'Basic',
    flex: 'Flex',
    onDemandSubscriptionInfo:
      'Get more out of your plan by enabling on-demand resource usage. <br/>You can customize these now or later from Cost management.',
    onDemandPriceCeiling: 'up to <price/> / mo',
    onDemandMinutesInfo:
      'Currently available {{minutes}} monthly on-demand process run minutes with <price/> cost per minute.',
    onDemandAssistantRunsInfo:
      'Currently available {{assistantRuns}} monthly on-demand assistant runs with <price/> cost per run.',
    plansGeneralInfoTitle: 'All tiers include',
    plansGeneralInfo: {
      workforce: 'Workforce for unattended automation',
      scheduler: 'Scheduler, API triggering and e-mail integration',
      dashboard: 'Executive and operational dashboards',
      workData: 'Work Data Management for process data',
      assistants: 'Assistants for attended automation',
      environments: 'Cloud workers',
      environmentGroups: 'Self-hosted worker groups',
      vault: 'Secure credentials vault',
    },
    planInfo: {
      dev1: {
        name: 'Developer',
        desc: 'Individual users and small businesses',
      },
      team1: {
        name: 'Team',
        desc: 'Teams and organizations that are scaling up',
      },
      pro1: {
        name: 'Pro',
        desc: 'Large operations with SLA support',
      },
      free1: {
        name: 'Free',
        desc: 'For new automation initiatives just starting out',
      },
      basic1: {
        name: 'Basic',
        desc: 'For running business-ready automations at scale',
      },
      pro2: {
        name: 'Pro',
        desc: 'For teams with advanced robot operations',
      },
      free10: {
        name: 'Free',
        desc: 'For new automation initiatives just starting out',
      },
      starter10: {
        name: 'Starter',
        desc: 'For running business-ready automations at scale',
      },
      flex10: {
        name: 'Flex',
        desc: 'Scale up your digital workforce with flexible cloud resources',
      },
      pro10: {
        name: 'Pro',
        desc: 'Scale up your digital workforce to support larger automations',
      },
      // {plan}20 tire
      free20: {
        name: 'Free',
        desc: 'Try out Control Room features and build simple automation for free',
        section_1: {
          title: '1 Workspace',
          info: [{ text: 'task run time limited to 3 minutes per run' }],
        },
        section_2: {
          title: 'Workforce',
          info: [
            { text: 'Up to 2 processes' },
            { text: '240 run minutes included' },
            { text: 'Extra minutes not available' },
            { text: '* 7 day work data retention' },
          ],
        },
        section_3: { title: 'Assistants', info: [{ text: '10 Assistant runs' }] },
      },
      starter20: {
        name: 'Starter',
        desc: 'Basic plan for operating lightweight automations',
        section_1: {
          title: 'Everything in Free plus',
          info: [
            { text: 'No run time limits' },
            { text: 'Parallel execution up to 10 workers in a single process' },
          ],
        },
        section_2: {
          title: 'Workforce',
          info: [
            { text: 'Up to 4 processes' },
            { text: '240 run minutes included' },
            { text: '$0.10 per extra minute' },
            { text: '30 day work data retention' },
          ],
        },
        section_3: {
          title: 'Assistants',
          info: [{ text: '50 Assistant runs' }, { text: '$0.20 per extra assistant run' }],
        },
      },
      flex20: {
        name: 'Flex',
        desc: 'Scalable plan suitable for most production needs',
        section_1: {
          title: 'Everything in Starter plus',
          info: [
            { text: 'Executive and operational dashboard' },
            { text: 'Self-hosted worker groups' },
            { text: '4 Workspaces' },
          ],
        },
        section_2: {
          title: 'Workforce',
          info: [
            { text: 'Up to 40 processes' },
            { text: '1440 run minutes included' },
            { text: '$0.07 per extra minute' },
            { text: '90 day work data retention' },
          ],
        },
        section_3: {
          title: 'Assistants',
          info: [{ text: '300 Assistant runs' }, { text: '$0.15 per extra assistant run' }],
        },
      },
      pro20: {
        name: 'Pro',
        desc: 'Powerful plan suitable for high volumes and advanced compliance & governance needs',
        section_1: {
          title: 'Everything in Flex plus',
          info: [],
        },
        section_2: {
          title: 'Available options',
          info: [
            { text: 'Run minutes and Assistant run volume discount and bundles' },
            { text: 'Billing caps for high-volume processes' },
            { text: 'Custom number of processes' },
            { text: 'Custom number of workspaces' },
            { text: 'Longer work data retention' },
            { text: 'Workspace level role-based access control (RBAC)' },
            { text: 'Self-hosted container worker' },
            { text: 'Dynamic VM provisioning' },
            { text: 'Priority SLA' },
            { text: 'Annual billing option' },
          ],
        },
      },
      free30: {
        name: 'Developer',
        desc: 'Experiment & build automations for free',
        section_1:
          'Create your first attended and unattended automations and get familiar with Control Room. Get free online training and certifications, community support, and more.',
        section_2: {
          title: 'Developer tier includes:',
          info: [
            { text: '240 run minutes' },
            { text: '1 workspace' },
            { text: '2 process limit per workspace' },
            { text: '1 parallel execution per process' },
            { text: '1 assistant user seat' },
            { text: 'Community and AI support' },
          ],
        },
        section_3: {},
      },
      starter30: {
        name: 'Starter',
        desc: 'Self-service tier that brings automation to everyone',
        section_1:
          'Fixed package for operating bots in production. Upgrade to more assistant users and unattended run minutes with enhanced parallel processing capabilities.',
        section_2: {
          title: 'Starter tier includes:',
          info: [
            { text: '1,500 run minutes' },
            { text: '2 workspaces' },
            { text: '5 process limit per workspace' },
            { text: '10 parallel executions per process' },
            { text: '5 assistant user seats' },
            { text: 'Community and AI support' },
          ],
        },
        section_3: {},
      },

      flex30: {
        name: 'Consumption',
        desc: 'We scale as you scale. Our most popular plan!',
        section_1: {
          title:
            'Ideal for organizations needing on-demand automation with consumption-based pricing and advanced features.',
          info: [],
        },
        section_2: {
          title: 'Consumption tier includes:',
          info: [
            { text: '$0.10 per minute' },
            { text: '20 workspaces' },
            { text: '200 processes' },
            { text: '20 parallel executions per process' },
            { text: 'Online support' },
          ],
        },
        section_3: {},
      },

      enterprise30: {
        name: 'Enterprise',
        desc: 'Enterprise governance, compliance and support.',
        section_1: {
          title:
            'Tailored for advanced governance, compliance, and custom support. Ideal for organizations needing scalable automation with custom configurations.',
          info: [],
        },
        section_2: {
          title: 'Enterprise tier includes:',
          info: [
            { text: 'Custom run minute packages' },
            { text: 'Unlimited workspaces' },
            { text: 'Unlimited processes' },
            { text: 'Up to unlimited parallel executions per process' },
            { text: 'Extended support and training' },
            {
              text: 'SSO integration, regional Control Rooms, custom RBAC, dedicated account manager add-on available, flexible run minute packages, enhanced governance and compliance features, regional control centers, multi-region deployments, and API access for advanced integration scenarios',
            },
          ],
        },
        section_3: {},
      },

      enterprise: {
        name: 'Enterprise',
        desc: 'Enterprise-grade governance, compliance and support',
        section_1: {
          title: 'Everything in Pro plus',
          info: [],
        },
        section_2: {
          title: 'Available options',
          info: [
            { text: 'Custom role-based access control (RBAC)' },
            { text: 'Single sign-on (SSO)' },
            { text: 'Custom data residency' },
            { text: 'Dedicated technical account manager' },
            { text: 'Enterprise support & training' },
          ],
        },
      },
    },
    comingSoon: 'Coming soon',
    lineItem: 'Line item',
    referenceNumber: 'Reference number',
    amount: 'Amount',
    receipt: 'Receipt',
    noInvoices: 'No invoices',
    downloadPdf: 'Download pdf',
    cancelInvite: 'Cancel invite',
    [UserMessages.ROLE_CHANGE_SUCCESSFUL]: 'Role change successful',
    [UserMessages.WORKSPACE_MEMBER_ADD_SUCCESSFUL]: 'Workspace member added',
    [UserMessages.TEST_EVENT_SENT]: 'Test event sent to endpoint',
    [OrganizationRoleNames.auditor]: 'Auditor',
    [OrganizationRoleNames.owner]: 'Owner',
    [OrganizationRoleNames.admin]: 'Administrator',
    [OrganizationRoleNames.data_analyst]: 'Data Analyst',
    [OrganizationRoleNames.member]: 'Member',
    [OrganizationRoleNames.operator]: 'Operator',
    [OrganizationRoleNames.publisher]: 'Publisher',
    workspaceRole: {
      auditor: 'Auditor',
      admin: 'Administrator',
      controller: 'Controller',
      editor: 'Developer',
      member: 'Operator',
      assistantUser: 'Assistant User',
      customAllyTechnicalManager: 'Ally Technical Manager',
      customAllySustain: 'Ally Sustain',
      customAllyDeveloper: 'Ally Developer',
      customAllyServiceAccount: 'Ally Service Account',
      customAllyAudit: 'Ally Audit',
      operator: 'Operator',
      knowledgeWorker: 'Knowledge Worker',
      customPmiConfigurator: 'Configurator',
      agentSupervisor: 'Supervisor',
      customKenvueSupport: 'Support',
    } satisfies Record<WorkspaceRoleId, string>,
    updatedFrom:
      'You upgraded your plan from {{previous}} to {{next}} in the middle of the billing cycle on {{date}}.',
    fullRateForPlan: 'The full rate for {{next}} plan is {{price}}.',
    wsRoleChangeNoPermissions: 'You do not have permissions to edit this role',
    wsRoleChangeLastAdmin: 'At least one administrator required',
    wsRoleChangeRolesDisabledForOrg: 'Workspace roles are not enabled in the current plan',
    wsRoleChangeDefault: 'Unable to edit role',
    orgRoleChangeNoPermissions: 'You do not have permissions to edit this role',
    orgRoleChangeLastOwner: 'At least one owner required',
    orgRoleChangeDefault: 'Unable to edit role',
    runExampleProcessInstruction:
      'Navigate to your example workspace and run the example process by clicking the play button. See the instructions below in a gif.',
    getFamiliar: 'Get familiar with basic concepts and terminology',
    docsSiteInstruction:
      'Our Docs site contains resources for all-the-things related to software robot development. Go to the Docs site and get familiar with Control Room key concepts.',
    visitRobocorpDocs: 'Visit Robocorp Docs',
    downloadInstallRobocorpLab: 'Download and install Robocorp Lab',
    robocorpLabDescription:
      'Robocorp Lab is an interactive development environment for software robot development. It is the first IDE purpose-built for Software Robot Development. Download Robocorp Lab to start building robots!',
    getStarted: 'Get started',
    howToGetStarted: 'How to get started',
    haveCompletedTask: 'I have completed this task',
    readyToGo: 'You are ready to go!',
    greatJob: 'Great job!',
    diveDeeper: "Now, let's dive deeper.",
    downloadTools: "Next, let's download the development tools.",
    startOrchestrating: 'Start orchestrating your Robot Workforce!',
    downloadRobocorpLab: 'Download Robocorp Lab',
    downloadRobocorpAssistant: 'Download Assistant App',
    assistants: 'Assistants',
    storage: 'Storage',
    webhooksKey: 'Webhooks',
    assistantsDesc:
      "Assistants tasks run on user's laptop and assist them in completing their tasks, regardless of department or systems.",
    readTheDocs: 'Read the documentation',
    giveUsFeedback: 'Give us feedback',
    featureNotavailable: 'This feature is not available on your current tier, please upgrade.',
    assistantDetails: 'Assistant Details',
    noAssistants: 'No assistants',
    deleteAssistant: 'Delete assistant',
    deleteAssistantText: 'Are you sure you want to delete the assistant?',
    newAssistant: 'New Assistant',
    createAssistant: 'Create Assistant',
    assistantName: 'Assistant Name',
    allOrgMembersAreInWorkspace: 'All organization members are already members in this workspace',
    addOrgMemberToWorkspace: 'Select organization member to add to this workspace',
    noRobotsForAssistant: 'No tasks available that support assistant',
    changeYourRole: 'Change your role',
    changeYourRoleOwnerText:
      'Are sure you want to change your organization role from {{previous}} to {{next}}? This can be only changed back by another owner.',
    changeYourRoleText:
      'Are sure you want to change your organization role from {{previous}} to {{next}}? This can be only changed back by another admin or owner.',
    changeRole: 'Change role',
    noAssistantRuns: 'No assistant runs',
    assistantRuns: 'Assistant runs',
    activityTemplateDesc: 'You can create software robots with Robocorp Lab.',
    getStartedHere: 'Get started here',
    fromGit: 'Public Git',
    gitRepository: 'Git Repository',
    github: 'GitHub',
    gitlab: 'GitLab',
    gitRefresh: 'Pull latest',
    refresh: 'Refresh',
    editURL: 'Edit Git URL',
    noGitSourceSet: 'Git source not set',
    packageUrlDesc: 'URL for a package zip file',
    processConfigurationPreferences: {
      workspaceMembersOrExternalMembers: 'Workspace members or external members',
      workspaceSelectionPlaceholder: 'Enter a name or email',
      externalMember: 'External member',
      noWorkspaceMemberMatch: 'No workspace members matches',
      allMembersAdded: 'All members already selected',
      noWorkspaceMemberMatchWithExternalMembers:
        'No workspace members matches. Allowed domains for external members: {{allowedEmailDomains}}',
    },
    fileType: {
      img: 'Image',
      html: 'HTML file',
      txt: 'Text file',
      pdf: 'PDF file',
      log: 'Log file',
      data: 'Data',
      spreadsheet: 'Spreadsheet',
      archive: 'Archive',
      configuration: 'Configuration',
    },
    share: 'Share',
    sharing: 'Sharing',
    shareWorkspace: 'Share Workspace',
    shareWorkspaceNoPermission: "You don't have permissions to share this Workspace",
    inviteMember: 'Invite Member',
    enterNameOrEmail: 'Enter Name or Email',
    enterEmail: 'Enter Email',
    addMember: 'Add member',
    wsMembersAmount: 'This workspace has {{workspaceMembersCount}} member(s)',
    assistantRunDetails: 'Assistant run details',
    renameAssistant: 'Rename assistant',
    editAssistant: 'Edit assistant',
    deleteAssistantRun: 'Delete assistant run',
    deleteAssistantRunText: 'Are you sure you want to delete this assistant run?',
    deleteAssistantRunText_incomplete:
      'The run is not completed. Are you sure you want to still delete this assistant run?',
    assistantRobotYmlOnly:
      'Assistants only support tasks with the new yaml configuration. <0>Read more about configuration here</0>.',
    readMore: 'Read more',
    lastRun: 'Last run',
    runCount: 'Run count',
    endTs: 'Ended',
    startedBy: 'Started by',
    noRuns: 'No runs',
    createdBy: 'Created by',
    sharedWith: 'Shared with',
    orgAndWsInviteSent: 'User invited to organization and workspace',
    workforce: 'Workforce',
    subject: 'Subject',
    subjectDesc: 'Try to be as precise as possible',
    ticketProblemDesc: 'Describe your problem...',
    createSupportTicket: 'Create a support ticket',
    ticketResponseDesc: 'We will get back to you by email.',
    ticketSent: 'Ticket sent',
    ticketSentDescSLA: 'We will contact you by email.',
    ticketSentDescOneDaySLA: 'We will contact you by email in one business day.',
    ticketSentNoSLA:
      'Thank you for reporting the issue. For more assistance you can use Robocorp Forum or our Community Slack.',
    you: 'You',
    changeYourWsRoleWarningText:
      'Are sure you want to change your workspace role? You might not be able to change it back.',
    renameRobot: 'Rename robot',
    nextRun: 'Next run',
    previousRun: 'Previous run',
    robotName: 'Robot name',
    noSchedule: 'No schedule',
    workItemFiles: 'Work item files',
    workData: 'Work data',
    data: 'Data',
    customizeColumns: 'Customize columns',
    noWorkItemFiles: 'No work item files',
    privacyCompliance: 'Privacy & Compliance',
    compliance: {
      updateDisabled: 'Updating the compliance settings has been disabled for this organization',
      header: 'Compliance',
      requireReason: 'Require a reason for stopping a Process Run',
      requireReasonDescription:
        'Require users to provide a reason when stopping or completing a Process Run',
      hideVaultSecretValues: 'Prevent viewing Vault secret values',
      hideVaultSecretValuesDescription:
        'Once created, do not show Vault secrets values in cleartext',
    },
    fillInPrivacyCompliance: 'Please fill in privacy and compliance information below.',
    fillInPrivacyComplianceText:
      'Is this organization processing data, or plans to process data, from EU/EEA residents on Robocorp platform?',
    dpaPartOfTerms: 'Our data processing terms are part of our',
    yes: 'Yes',
    no: 'No',
    cronDesc: 'Define a custom schedule using a CRON expression',
    addCronSchedule: 'Add cron schedule',
    cronExpression: 'Expression',
    cronExpressionError: 'Invalid CRON expression: it should contain exactly 5 groups',
    scheduleCron: 'Custom cron trigger',
    editCron: 'Edit cron schedule',
    workspaceKey: 'API Key',
    workspaceApiAccess: 'Workspace API Access',
    workspaceKeys: 'API Keys',
    workspaceKeysDesc: 'API keys to interact with Control Room API',
    workspaceKeyTypes: {
      processes: 'Processes',
      robots: 'Tasks',
      assistants: 'Assistants',
      workers: 'Workers',
      storage: 'Assets',
      webhooks: 'Webhooks',
      vault: 'Vault',
    },
    loadingWorkspaceKeys: 'Loading API keys',
    general: 'General',
    unusedKey: 'Unused',
    read: 'Read',
    write: 'Write',
    trigger: 'Trigger',
    inviteUsers: 'Invite users',
    readUsers: 'Read users',
    removeUsers: 'Remove users',
    editRole: 'Edit role',
    uses: 'Uses',
    lastUsed: 'Last used',
    keyName: 'Key name',
    enterName: 'Enter name',
    deleteWorkspaceKey: 'Delete API Key',
    deleteWorkspaceKeyText: 'Are you sure you want to delete the key "{{name}}"?',
    revokeAll: 'Revoke all',
    revokeAllKeys: 'Revoke all keys',
    revokeKeysText: 'Are you sure you want to revoke all keys?',
    deleteAll: 'Delete all',
    now: 'Now',
    access: 'Access',
    noWorkspaceKeys: 'No API keys',
    readRuns: 'Read runs',
    readRunOutputs: 'Read run outputs',
    triggerProcesses: 'Trigger process runs',
    triggerProcessesNotAvailable: 'Triggering processes with API is not available on your tier',
    readRobots: 'Read task packages',
    writeRobots: 'Write task packages',
    runMinsDesc:
      'With on-demand task run minutes you can run additional minutes on top of your chosen plan.',
    assistantRunsDesc:
      'With on-demand assistant runs you can run additional assistant runs on top of your chosen plan.',
    upgradePlanToActivate: 'Upgrade your plan to activate',
    upgradePlanToIncreaseWorkspacesLimit:
      'Upgrade your organization’s plan to add more workspaces.',
    upgradePlan: 'Upgrade plan',
    apiAccess: 'API Access',
    apiAccessDesc: 'Run and manage processes and robots via API.',
    api: 'API',
    exploreRobots: 'Explore robots',
    havingTrouble: 'Having trouble?',
    googleAuthUser: 'User authenticated via Google',
    ssoAuthUser: 'User authenticated via SSO',
    mfaEnrolled:
      'Multi-Factor-Authentication (MFA) is enabled for this user since {{mfaEnrolledAt}}',
    getStartedTutorial: 'Get started checklist',
    log2: 'Log',
    wsAuditLog: 'Workspace Audit Log',
    orgAuditLog: 'Organization Audit Log',
    auditLog: 'Audit Log',
    noAuditLogItems: 'No audit log items',
    loadingAuditLogItems: 'Loading audit log items',
    noValueSpecified: 'No value specified',
    [RobocloudEventType.ActivityCreated]: 'Activity Created',
    [RobocloudEventType.ActivityTemplateCreated]: 'Robot Created',
    [RobocloudEventType.ActivityTemplateDeleted]: 'Robot Deleted',
    [RobocloudEventType.ActivityTemplateUpdated]: 'Robot Updated',
    [RobocloudEventType.ProcessCreated]: 'Process Created',
    [RobocloudEventType.UserCreated]: 'User Created',
    [RobocloudEventType.WorkspaceCreated]: 'Workspace Created',
    [RobocloudEventType.WorkspaceInvitationAccepted]: 'Workspace Invitation Accepted',
    [RobocloudEventType.WorkspaceInvitationCreated]: 'Workspace Invitation Created',
    [RobocloudEventType.ProcessRunStarted]: 'Process Run Started',
    [RobocloudEventType.ProcessRunStopping]: 'Process Run Terminated',
    [RobocloudEventType.ActivityRunStarted]: 'Robot Run Started',
    [RobocloudEventType.ActivityRunUpdated]: 'Robot Run Updated',
    [RobocloudEventType.WorkItemCompleted]: 'Work Item done',
    [RobocloudEventType.UserLoggedIn]: 'User Logged In',
    [RobocloudEventType.WorkspaceDeleted]: 'Workspace Deleted',
    [RobocloudEventType.WorkspaceDataDeleted]: 'Workspace Data Deleted',
    [RobocloudEventType.OrganizationCreated]: 'Organization Created',
    [RobocloudEventType.OrganizationLimitsBreached]: 'Organization Limits Breached',
    [RobocloudEventType.WorkerCreated]: 'Worker Created',
    [RobocloudEventType.WorkerDeleted]: 'Worker Deleted',
    [RobocloudEventType.AccessCredentialDeleted]: 'Access Credential Deleted',
    [RobocloudEventType.AccessCredentialCreated]: 'Access Credential Created',
    [RobocloudEventType.AccessCredentialUsed]: 'Access Credential Used',
    [RobocloudEventType.SubscriptionPlanChanged]: 'Subscription Plan Changed',
    [RobocloudEventType.SubscriptionCreated]: 'Subscription Created',
    [RobocloudEventType.SubscriptionRenewalError]: 'Subscription Renewal Error',
    [RobocloudEventType.SubscriptionCanceled]: 'Subscription Cancelled',
    [RobocloudEventType.SubscriptionRenewed]: 'Subscription Renewed',
    [RobocloudEventType.RunMinuteLimitHit]: 'Run Minute Limit Hit',
    [RobocloudEventType.ResourceUsageReset]: 'Resource Usage Reset',
    [RobocloudEventType.UserLoggedOut]: 'User Logged Out',
    [RobocloudEventType.OrganizationDeleted]: 'Organization Deleted',
    [RobocloudEventType.OrganizationInviteSent]: 'Organization Invite Sent',
    [RobocloudEventType.OrganizationInviteAccepted]: 'Organization Invite Accepted',
    [RobocloudEventType.OrganizationMemberRemoved]: 'Organization Member Removed',
    [RobocloudEventType.ActivityRunCompleted]: 'Robot Run Completed',
    [RobocloudEventType.ProcessRunCompleted]: 'Process Run Completed',
    [RobocloudEventType.RunMinutesUsed]: 'Run Minutes Used',
    [RobocloudEventType.OrganizationUpdated]: 'Organization Updated',
    [RobocloudEventType.ActivityTemplateFileUpdated]: 'Robot File Updated',
    [RobocloudEventType.AssistantCreated]: 'Assistant Created',
    [RobocloudEventType.AssistantRunCompleted]: 'Assistant Run Completed',
    [RobocloudEventType.VaultSecretCreated]: 'Vault Secret Created',
    [RobocloudEventType.VaultSecretAccessed]: 'Vault Secret Accessed',
    [RobocloudEventType.WorkspaceKeyUsed]: 'Workspace Key Used',
    [RobocloudEventType.WorkspaceKeyCreated]: 'Workspace Key Created',
    [RobocloudEventType.WorkspaceKeyDeleted]: 'Workspace Key Deleted',
    [RobocloudEventType.WorkspaceKeyUpdated]: 'Workspace Key Updated',
    [RobocloudEventType.WorkItemFileUploaded]: 'Work Item File Uploaded',
    [RobocloudEventType.WorkItemFileDownloaded]: 'Work Item File Downloaded',
    [RobocloudEventType.WorkItemDataRead]: 'Work Item Data Read',
    [RobocloudEventType.WorkItemDataWrite]: 'Work Item Data Write',
    [RobocloudEventType.WorkItemDeleted]: 'Work Item Deleted',
    [RobocloudEventType.OnDemandMinutesEnabled]: 'On-Demand Minutes Enabled',
    [RobocloudEventType.OnDemandMinutesDisabled]: 'On-Demand Minutes Disabled',
    [RobocloudEventType.RunMinuteUsageWarning]: 'Run Minute Usage Warning',
    [RobocloudEventType.ProcessRunDeleted]: 'Process Run Deleted',
    toBeImplemented: 'To be implemented',
    [`auditLog${RobocloudEventType.ActivityCreated}`]: 'Audit Log explanation for Activity Created',
    [`auditLog${RobocloudEventType.ActivityTemplateCreated}`]:
      'User {{ user }} created task package {{ robot }}',
    [`auditLog${RobocloudEventType.ActivityTemplateDeleted}`]:
      'User {{ user }} deleted task package {{ robot }}',
    [`auditLog${RobocloudEventType.ActivityTemplateUpdated}`]:
      'Audit Log explanation for Task Package Updated',
    [`auditLog${RobocloudEventType.ProcessCreated}`]:
      'User {{ userId }} created process {{ process }}',
    [`auditLog${RobocloudEventType.UserCreated}`]: 'Audit Log explanation for User Created',
    [`auditLog${RobocloudEventType.WorkspaceCreated}`]:
      'Audit Log explanation for Workspace Created',
    [`auditLog${RobocloudEventType.WorkspaceInvitationAccepted}`]:
      'Audit Log explanation for Workspace Invitation Accepted',
    [`auditLog${RobocloudEventType.WorkspaceInvitationCreated}`]:
      'Audit Log explanation for Workspace Invitation Created',
    [`auditLog${RobocloudEventType.ProcessRunStarted}`]:
      'Process {{ process }} started by {{ startedBy }}',
    [`auditLog${RobocloudEventType.ProcessRunStopping}`]:
      'Process {{ process }} terminated by {{ startedBy }}',
    [`auditLog${RobocloudEventType.ActivityRunStarted}`]:
      'Task run started in {{ runtimeType }} worker',
    [`auditLog${RobocloudEventType.ActivityRunUpdated}`]:
      'Audit Log explanation for Task Run Updated',
    [`auditLog${RobocloudEventType.WorkItemCompleted}`]: 'Audit Log explanation for Work Item Done',
    [`auditLog${RobocloudEventType.UserLoggedIn}`]: 'Audit Log explanation for User Logged In',
    [`auditLog${RobocloudEventType.WorkspaceDeleted}`]:
      'Audit Log explanation for Workspace Deleted',
    [`auditLog${RobocloudEventType.WorkspaceDataDeleted}`]:
      'Audit Log explanation for Workspace Data Deleted',
    [`auditLog${RobocloudEventType.OrganizationCreated}`]:
      'Audit Log explanation for Organization Created',
    [`auditLog${RobocloudEventType.OrganizationLimitsBreached}`]:
      'Audit Log explanation for Organization Limits Breached',
    [`auditLog${RobocloudEventType.AccessCredentialDeleted}`]:
      'Audit Log explanation for Access Credential Deleted',
    [`auditLog${RobocloudEventType.AccessCredentialCreated}`]:
      'Audit Log explanation for Access Credential Created',
    [`auditLog${RobocloudEventType.AccessCredentialUsed}`]:
      'Audit Log explanation for Access Credential Used',
    [`auditLog${RobocloudEventType.SubscriptionPlanChanged}`]:
      'Subscription plan was changed from {{ oldPlan }} to {{ newPlan }}',
    [`auditLog${RobocloudEventType.SubscriptionCreated}`]:
      'Audit Log explanation for Subscription Created',
    [`auditLog${RobocloudEventType.SubscriptionRenewalError}`]:
      'Subscription renewal failed, (attempt: {{ attemptCount }})',
    [`auditLog${RobocloudEventType.SubscriptionCanceled}`]:
      '{{ plan }} subscription has been canceled',
    [`auditLog${RobocloudEventType.SubscriptionRenewed}`]:
      '{{ plan }} subscription has been renewed',
    [`auditLog${RobocloudEventType.RunMinuteLimitHit}`]:
      'Organization run minute limit has been hit',
    [`auditLog${RobocloudEventType.ResourceUsageReset}`]:
      'Audit Log explanation for Resource Usage Reset',
    [`auditLog${RobocloudEventType.UserLoggedOut}`]: 'Audit Log explanation for User Logged Out',
    [`auditLog${RobocloudEventType.OrganizationDeleted}`]:
      'Audit Log explanation for Organization Deleted',
    [`auditLog${RobocloudEventType.OrganizationInviteSent}`]:
      'Audit Log explanation for Organization Invite Sent',
    [`auditLog${RobocloudEventType.OrganizationInviteAccepted}`]:
      'Audit Log explanation for Organization Invite Accepted',
    [`auditLog${RobocloudEventType.OrganizationMemberRemoved}`]:
      'Audit Log explanation for Organization Member Removed',
    [`auditLog${RobocloudEventType.ActivityRunCompleted}`]:
      'Step {{ step }} ran in {{  runtimeType }} worker and completed with result {{  result }}',
    [`auditLog${RobocloudEventType.ProcessRunCompleted}`]:
      'Process {{ process }} completed with result {{  result }} {{ code }}',
    [`auditLog${RobocloudEventType.RunMinutesUsed}`]:
      'Process {{ process }} used {{ minutes }} run minute(s)',
    [`auditLog${RobocloudEventType.RunMinutesUsed}org`]:
      'Process {{ process }} used {{ minutes }} run minute(s) in workspace {{ workspace }}',
    [`auditLog${RobocloudEventType.OrganizationUpdated}`]:
      'Audit Log explanation for Organization Updated',
    [`auditLog${RobocloudEventType.ActivityTemplateFileUpdated}`]:
      'Audit Log explanation for Task Package File Updated',
    [`auditLog${RobocloudEventType.AssistantCreated}`]:
      'User {{ userId }} created assistant {{ assistant }}',
    [`auditLog${RobocloudEventType.AssistantRunCompleted}`]:
      'Audit Log explanation for Assistant Run Completed',
    [`auditLog${RobocloudEventType.VaultSecretCreated}`]:
      'Audit Log explanation for Vault Secret Created',
    [`auditLog${RobocloudEventType.VaultSecretAccessed}`]:
      'Audit Log explanation for Vault Secret Accessed',
    [`auditLog${RobocloudEventType.WorkspaceKeyUsed}`]:
      'Audit Log explanation for Workspace Key Used',
    [`auditLog${RobocloudEventType.WorkspaceKeyCreated}`]:
      'Audit Log explanation for Workspace Key Created',
    [`auditLog${RobocloudEventType.WorkspaceKeyDeleted}`]:
      'Audit Log explanation for Workspace Key Deleted',
    [`auditLog${RobocloudEventType.WorkspaceKeyUpdated}`]:
      'Audit Log explanation for Workspace Key Updated',
    [`auditLog${RobocloudEventType.WorkItemFileUploaded}`]:
      'Audit Log explanation for Work Item File Uploaded',
    [`auditLog${RobocloudEventType.WorkItemFileDownloaded}`]:
      'Audit Log explanation for Work Item File Downloaded',
    [`auditLog${RobocloudEventType.WorkItemDataRead}`]:
      'Audit Log explanation for Work Item Data Read',
    [`auditLog${RobocloudEventType.WorkItemDataWrite}`]:
      'Audit Log explanation for Work Item Data Write',
    [`auditLog${RobocloudEventType.WorkItemDeleted}`]:
      'Work Item deleted from process {{ process }}',
    [`auditLog${RobocloudEventType.ProcessRunDeleted}`]:
      'Process run {{ runNo }} deleted from process {{ process }}',
    [`auditLog${RobocloudEventType.OnDemandMinutesEnabled}`]:
      'On-Demand minutes were enabled or modified',
    [`auditLog${RobocloudEventType.OnDemandMinutesDisabled}`]: 'On-Demand minutes were disabled',
    [`auditLog${RobocloudEventType.RunMinuteUsageWarning}nearing_exhaustion`]:
      'Run minutes are nearing the limit. {{ usedMinutes }} out of {{ maxMinutes }} minutes have been used',
    [`auditLog${RobocloudEventType.RunMinuteUsageWarning}exhausted`]:
      'Run minutes have hit the limit. {{ usedMinutes }} out of {{ maxMinutes }} minutes have been used',
    [`auditLog${RobocloudEventType.RunMinuteUsageWarning}ondemand_nearing_exhaustion`]:
      'On-Demand run minutes are nearing the limit. {{ usedMinutes }} out of {{ maxMinutes }} minutes have been used',
    [`auditLog${RobocloudEventType.RunMinuteUsageWarning}ondemand_exhausted`]:
      'On-Demand run minutes have hit the limit. {{ usedMinutes }} out of {{ maxMinutes }} minutes have been used',
    [`auditLog${RobocloudEventType.WorkerCreated}`]: 'Worker {{ name }} created',
    [`auditLog${RobocloudEventType.WorkerDeleted}`]: 'Worker {{ name }} deleted',
    // TODO: can't update common, so just hardcoded the event types I saw
    [`newAuditLog${`WorkspaceUserRoleChanged`}`]: 'Workspace User Role Changed',
    [`newAuditLog${`OrganizationUserRoleChanged`}`]: 'Organization User Role Changed',
    [`newAuditLog${`WebhookViewed`}`]: 'Webhook secret viewed',
    [`newAuditLog${`WebhookCreated`}`]: 'Webhook created',
    [`newAuditLog${`WebhookUpdated`}`]: 'Webhook updated',
    [`newAuditLog${`WebhookDeleted`}`]: 'Webhook deleted',
    [`newAuditLog${`WebhookInvoked`}`]: 'Webhook invoked',
    [`newAuditLog${`WorkspaceKeyViewed`}`]: 'API Key secret viewed',
    [`newAuditLog${`AssistantDeleted`}`]: 'Assistant Deleted',
    [`newAuditLog${`AssistantUpdated`}`]: 'Assistant Updated',
    [`newAuditLog${`EnvironmentLinkTokenCreated`}`]: 'Worker link token created',
    [`newAuditLog${`PaymentMethodDeleted`}`]: 'Payment method deleted',
    [`newAuditLog${`PaymentMethodUpdated`}`]: 'Payment method updated',
    [`newAuditLog${`BillingDataModified`}`]: 'Billing data updated',
    [`newAuditLog${`WorkspaceUserRemoved`}`]: 'Workspace user removed',
    [`newAuditLog${`ProcessUpdated`}`]: 'Process updated',
    [`newAuditLog${`WorkerUpdated`}`]: 'Worker updated',
    [`newAuditLog${`VaultSecretDeleted`}`]: 'Vault secret deleted',
    [`newAuditLog${`IntegrationAdded`}`]: 'Integration added',
    [`newAuditLog${`IntegrationUpdated`}`]: 'Integration updated',
    [`newAuditLog${`IntegrationRemoved`}`]: 'Integration removed',
    [`newAuditLog${`OrganizationAuditLogViewed`}`]: 'Organization audit log viewed',
    [`newAuditLog${`WorkspaceAuditLogViewed`}`]: 'Workspace audit log viewed',
    [`newAuditLog${`OrganizationAuditLogExported`}`]: 'Organization audit log exported',
    [`newAuditLog${`WorkspaceAuditLogExported`}`]: 'Workspace audit log exported',
    [`newAuditLog${RobocloudEventType.SubscriptionPlanChanged}`]: 'Subscription plan changed',
    [`newAuditLog${RobocloudEventType.SubscriptionRenewed}`]: 'Subscription renewed',
    [`newAuditLog${RobocloudEventType.OnDemandMinutesEnabled}`]:
      'On-Demand minutes enabled or modified',
    [`newAuditLog${RobocloudEventType.OnDemandMinutesDisabled}`]: 'On-Demand minutes disabled',
    [`newAuditLog${RobocloudEventType.OnDemandAssistantRunsEnabled}`]:
      'On-Demand assistant runs enabled or modified',
    [`newAuditLog${RobocloudEventType.OnDemandAssistantRunsDisabled}`]:
      'On-Demand assistant runs disabled',
    [`newAuditLog${RobocloudEventType.RunMinutesUsed}`]: 'Run minutes used',
    [`newAuditLog${RobocloudEventType.AssistantRunsUsed}`]: 'Assistant run used',
    [`newAuditLog${RobocloudEventType.RunMinuteUsageWarning}`]: 'Run minutes warning sent',
    [`newAuditLog${RobocloudEventType.AssistantRunWarning}`]: 'Assistant runs warning sent',
    [`newAuditLog${RobocloudEventType.RunMinuteLimitHit}`]: 'Run minute limit hit',
    [`newAuditLog${RobocloudEventType.AssistantRunLimitHit}`]: 'Assistant run limit hit',
    [`newAuditLog${RobocloudEventType.OrganizationCreated}`]: 'Organization created',
    [`newAuditLog${RobocloudEventType.OrganizationUpdated}`]: 'Organization updated',
    [`newAuditLog${RobocloudEventType.WorkspaceCreated}`]: 'Workspace created',
    [`newAuditLog${RobocloudEventType.WorkspaceUpdated}`]: 'Workspace updated',
    [`newAuditLog${RobocloudEventType.WorkspaceDeleted}`]: 'Workspace deleted',
    [`newAuditLog${RobocloudEventType.WorkspaceDataDeleted}`]: 'Workspace data deleted',
    [`newAuditLog${RobocloudEventType.WorkerCreated}`]: 'Worker created',
    [`newAuditLog${RobocloudEventType.WorkerDeleted}`]: 'Worker deleted',
    [`newAuditLog${RobocloudEventType.EnvironmentGroupCreated}`]: 'Worker group created',
    [`newAuditLog${RobocloudEventType.EnvironmentGroupUpdated}`]: 'Worker group updated',
    [`newAuditLog${RobocloudEventType.EnvironmentGroupDeleted}`]: 'Worker group deleted',
    [`newAuditLog${RobocloudEventType.ProcessCreated}`]: 'Process created',
    [`newAuditLog${RobocloudEventType.ProcessDeleted}`]: 'Process deleted',
    [`newAuditLog${RobocloudEventType.ActivityTemplateCreated}`]: 'Task package created',
    [`newAuditLog${RobocloudEventType.ActivityTemplateUpdated}`]: 'Task package updated',
    [`newAuditLog${RobocloudEventType.ActivityTemplateFileUpdated}`]: 'Task package file updated',
    [`newAuditLog${RobocloudEventType.ActivityTemplateDeleted}`]: 'Task package deleted',
    [`newAuditLog${RobocloudEventType.OrganizationMemberRemoved}`]: 'Organization user removed',
    [`newAuditLog${RobocloudEventType.WorkspaceInvitationCreated}`]: 'User invited to workspace',
    [`newAuditLog${RobocloudEventType.OrganizationInviteSent}`]: 'User invited to organization',
    [`newAuditLog${RobocloudEventType.WorkspaceKeyCreated}`]: 'API Key created',
    [`newAuditLog${RobocloudEventType.WorkspaceKeyUpdated}`]: 'API Key updated',
    [`newAuditLog${RobocloudEventType.WorkspaceKeyDeleted}`]: 'API Key deleted',
    [`newAuditLog${RobocloudEventType.VaultSecretCreated}`]: 'Vault Secret created or updated',
    [`newAuditLog${RobocloudEventType.VaultSecretAccessed}`]: 'Vault Secret accessed',
    [`newAuditLog${RobocloudEventType.AssetCreated}`]: 'Asset Item created',
    [`newAuditLog${RobocloudEventType.AssetUpdated}`]: 'Asset Item updated',
    [`newAuditLog${RobocloudEventType.AssetDeleted}`]: 'Asset Item deleted',
    [`newAuditLog${RobocloudEventType.RbacSSORuleCreated}`]: 'SSO RBAC Rule created',
    [`newAuditLog${RobocloudEventType.RbacSSORuleUpdated}`]: 'SSO RBAC Rule updated',
    [`newAuditLog${RobocloudEventType.RbacSSORuleDeleted}`]: 'SSO RBAC Rule deleted',
    [`newAuditLog${`AssistantRunStarted`}`]: 'Assistant run started',
    [`newAuditLog${`AssistantCreated`}`]: 'Assistant created',
    [`newAuditLog${`AuditProcessRunStarted`}`]: 'Process run started',
    [`newAuditLog${`AuditProcessRunDeleted`}`]: 'Process run deleted',
    [`newAuditLog${`AuditProcessRunCompleted`}`]: 'Process run completed',
    [`newAuditLog${`AuditProcessRunViewed`}`]: 'Process run viewed',
    [`newAuditLog${`AuditProcessRunStopRequested`}`]: 'Process run stop requested',
    [`newAuditLog${`AuditProcessRunCompletionRequested`}`]: 'Process run completion requested',
    [`newAuditLog${`UserLoggedIn`}`]: 'User logged in',
    [`newAuditLog${`UserLoggedOut`}`]: 'User logged out',
    [`resourceType${`User`}`]: 'User',
    [`resourceType${`Webhook`}`]: 'Webhook',
    [`resourceType${`Organization`}`]: 'Organization',
    [`resourceType${`Workspace`}`]: 'Workspace',
    [`resourceType${`Assistant`}`]: 'Assistant',
    [`resourceType${`AssistantRun`}`]: 'Assistant Run',
    [`resourceType${`Process`}`]: 'Process',
    [`resourceType${`ProcessRun`}`]: 'Process Run',
    [`resourceType${`StepRun`}`]: 'Step Run',
    [`resourceType${`WorkItem`}`]: 'Work Item',
    [`resourceType${`VaultSecret`}`]: 'Vault Secret',
    [`resourceType${`AssetItem`}`]: 'Asset',
    [`resourceType${`SSORbacRule`}`]: 'SSO RBAC Rule',
    [`resourceType${`Environment`}`]: 'Worker',
    [`resourceType${`EnvironmentGroup`}`]: 'Worker Group',
    [`resourceType${`EnvironmentLinkToken`}`]: 'Worker Link Token',
    [`resourceType${`BillingData`}`]: 'Billing Data',
    [`resourceType${`PaymentMethod`}`]: 'Payment Method',
    [`resourceType${`Robot`}`]: 'Task Package',
    [`resourceType${`Integration`}`]: 'Integration',
    [`resourceType${`WorkspaceKey`}`]: 'API Key',
    [`resourceType${`RunMinutes`}`]: 'Run Minutes',
    [`resourceType${`AssistantRuns`}`]: 'Assistant Runs',
    [`resourceType${`Subscription`}`]: 'Subscription',
    [`resourceType${`WorkspaceInvite`}`]: 'Workspace Invite',
    [`resourceType${`OrganizationInvite`}`]: 'Organization Invite',
    [`resourceType${`AuditLog`}`]: 'Audit Log',
    [WorkItemLogEventType.WorkItemCreated]: 'Work item created',
    [WorkItemLogEventType.WorkItemWaiting]: 'Work item waiting',
    [WorkItemLogEventType.WorkItemProcessing]: 'Work item processing',
    [WorkItemLogEventType.WorkItemFailed]: 'Work item failed',
    [WorkItemLogEventType.WorkItemDone]: 'Work item done',
    [WorkItemLogEventType.WorkItemRetried]: 'Work item retried',
    [WorkItemLogEventType.WorkItemPayloadUpdated]: 'Work item payload updated',
    [WorkItemLogEventType.WorkItemFileAdded]: 'Work item filed added',
    [WorkItemLogEventType.WorkItemFileDeleted]: 'Work item file deleted',
    auditLogNoDetails: 'No details provided',
    event: 'Event',
    API: 'API',
    APIHelper: {
      title: 'API Helper',
      labelOld: 'Old',
      description: 'Use the helper below to access our APIs easier.',
      oldDescription: 'This version of the API has been deprecated, please use the new version.',
      additionalParameters: 'Additional parameters',
      pathParameters: 'Path parameters',
      queryParameters: 'Query parameters',
      includeData: 'Include Work data payload',
      limit: 'Maximum number of items returned',
      size: 'Number of items returned at once',
      state: 'State',
      search: 'Search for string in payload',
      sortBy: 'Sort by',
      sortOrder: 'Sort order',
      cursor: 'Cursor',
      startDate: 'Start Date',
      endDate: 'End Date',
      chooseKey: 'API Key',
      requestingKey: 'Requesting API key',
      createKey: 'Create API Key',
      noKeyWithPermission: 'No API key with permission "{{ permission }}"',
      executeRequest: 'Execute request',
      requestResponse: 'Request response',
      showApiKey: 'Show API key',
    },
    inProgress: 'In progress',
    failedData: 'Failed data',
    completedData: 'Completed data',
    loadingWorkItems: 'Loading work data',
    noWorkData: 'No work data',
    clearSelected: 'Clear selected',
    runSelected: 'Run selected',
    workDataStatus: 'Work Data Status',
    manageData: 'Manage data',
    workDataPayload: 'Work data payload',
    loadingWorkDataStatus: 'Loading work data status...',
    selectedToRunAmount: '{{number}} selected',
    noPayload: 'No payload',
    openInNewTab: 'Open in new tab',
    processStatus: 'Process status',
    runStatus: 'Run status',
    runMinutesUsed: 'Run Minutes',
    workItemOkCount: 'Successful work items',
    workItemErrCount: 'Failed work items',
    runOkCount: 'Successful runs',
    runErrCount: 'Failed runs',
    lastResult: 'Last result',
    lastRunTs: 'Last run',
    itemSuccessRate: 'Success rate',
    runSuccessRate: 'Run success rate',
    readProcesses: 'Read processes',
    writeProcesses: 'Write processes',
    defaultRun: 'Default run',
    emptyRun: 'Empty work item',
    emptyRunDescription: 'Run process with empty work item as input',
    customDataRun: 'Custom work item',
    customDataRunDescription: 'Run process with custom work item data',
    workDataRun: 'Batch run work items',
    workDataRunDescription: 'Run process with multiple work items as input',
    allInputData: 'All input data',
    allFailedData: 'All failed data',
    reset: 'Reset',
    prcoessStatusReset: 'Process status reset',
    prcoessStatusResetConfirm: 'Confirm to reset process status information',
    trackPeriod: 'Track period',
    customRun: 'Run with input data',
    webhookProcess: 'Process: ',
    webhookProcessNotFound: 'Process not found',
    webhooksDesc: 'Use webhooks to integrate Control Room with third party applications',
    webhooksEventsDesc: 'Select the applicable events for the generated webhook',
    webhooksEventsToSend: 'Events to send',
    webhook: 'Webhook',
    loadingWebhooks: 'Loading Webhooks',
    endpoint: 'Endpoint',
    endpointUrl: 'Endpoint URL',
    enterEndpointUrl: 'Type URL',
    deleteWebhook: 'Delete Webhook',
    deletingWebhook: 'Deleting Webhook...',
    addWebhook: 'New Webhook',
    deleteWebhookText: 'Are you sure you want to delete the webhook with endpoint: "{{endpoint}}"?',
    viewSecret: 'View Secret',
    webhookSecret: 'Webhook Secret',
    noWebhooks: 'No Webhooks',
    invalidUrl: 'Invalid URL',
    notSet: 'Not set',
    inputs: 'Inputs',
    outputs: 'Outputs',
    [`auditLog${RobocloudEventType.RunMinuteUsageWarning}`]:
      'Audit Log explanation for Run Minute Usage Warning',
    methodNotChosen: 'Method not chosen...',
    choose: 'Choose',
    selectApiOptionPrompt: 'What do you want to do?',
    startProcess: 'Start process',
    startProcessWithTriggers: 'Start the process with triggers',
    listProcessRuns: 'List process runs',
    robotApi: 'Robot API',
    processApi: {
      title: 'Process API',
      listProcesses: 'List processes',
      startProcessSinglePayload: 'Start process with a single work item payload',
      startBatchPayloadProcess: 'Start process with multiple work item payloads',
      startDefaultProcess: 'Start process with default configuration',
      stopProcessRun: 'Stop process run',
      listWorkspaceProcessRunsApiHelper: 'List process runs (for a workspace)',
      listProcessRunsApiHelper: 'List process runs (for a process)',
      getProcessRunStatus: 'Get process run status',
      getProcessRunWorkItemsSummary: 'Get process run work items summary',
      getWorkItem: 'Get work item',
      deleteWorkItem: 'Delete work item',
      retryWorkItem: 'Retry work item',
      createWorkItemInput: 'Create input work item',
      updateWorkItemPayload: 'Update work item payload',
      getWorkItemFileDownloadLink: 'Get work item file download link',
      getWorkItemFileUploadLink: 'Get work item file upload link',
      completeWorkItem: 'Mark work item as done',
      listRobotRuns: 'List robot runs',
      getRobotRun: 'Get robot run',
      listRobotRunEvents: 'List robot run events',
      listRobotRunConsoleMessages: 'List robot run console messages',
      listRobotRunArtifacts: 'List robot run artifacts',
      getRobotArtifact: 'Get robot artifact',
      listProcessRunWorkItems: 'List process run work items',
      listProcessWorkItems: 'List process work items',
    },
    workerApi: {
      title: 'Worker API',
      getWorker: 'Get worker',
      deleteWorker: 'Delete worker',
      deleteWorkerGroup: 'Delete worker group',
      updateWorker: 'Update worker',
      chooseWorker: 'Choose worker',
      chooseLinkToken: 'Choose link token',
      noWorkers: 'No workers',
      workerGroupId: 'Worker group id',
      linkTokenId: 'Link token id',
      createWorkerGroupToken: 'Create worker group link token',
      createWorkerGroup: 'Create worker group',
      noWorkerGroupLinkTokens: 'No worker group link tokens',
      listWorkerGroupLinkTokens: 'List worker group link tokens',
      deleteWorkerGroupLinkToken: 'Delete worker group link token',
      chooseWorkerGroup: 'Choose worker group',
      chooseWorkerGroupLinkToken: 'Choose worker group link token',
      updateWorkerGroupLinkToken: 'Update worker group link token',
      getWorkerGroupLinkToken: 'Get worker group link token',
      noWorkerGroups: 'No worker groups',
      listWorkers: 'List workers',
      getWorkerGroup: 'Get worker group',
      updateWorkerGroup: 'Update worker group',
      groupId: 'Worker Group ID',
      workerId: 'Worker ID',
      listWorkerGroups: 'List worker groups',
      addWorkerToGroup: 'Add worker to group',
      removeWorkerFromGroup: 'Remove worker from group',
      generateLinkToken: 'Create worker link token',
    },
    getLinkToUploadRobot: 'Get link to upload task package',
    getLinkToDownloadRobot: 'Get link to download task package',
    generatedApiCurlDesc: 'Here is a generated command',
    copy: 'Copy',
    server: 'Server',
    robotId: 'Robot ID',
    processId: 'Process ID',
    chooseProcess: 'Choose process',
    seeRobocorpDocs: 'See Robocorp docs',
    noRunsForProcess: 'No runs for selected process',
    chooseProcessRun: 'Choose process run',
    processRunId: 'Process run ID',
    chooseRobot: 'Choose robot',
    chooseRobotRun: 'Choose robot run',
    chooseRobotTask: 'Choose robot task',
    defaultTask: 'Default Task',
    chooseArtifact: 'Choose artifact',
    artifactId: 'Artifact ID',
    robotRunId: 'Robot run ID',
    noArtifactsForRun: 'no artifacts',
    noRobotRunsForProcess: 'no task runs',
    fileName: 'file name',
    runDefault: 'Run',
    runDefaultExplanation: 'Run the process with default configuration',
    runCustom: 'Run with input data',
    runCustomExplanation: 'Define input data you want run the process with',
    runDefaultTooltip: 'Runs the process once with default configuration of the process',
    runCustomTooltip: 'Runs the process once with given custom input',
    dataStatus: 'Data status',
    status: 'Status',
    sortByname: 'By name',
    sortByupdated: 'Last updated',
    sortByusage: 'Usage',
    authorUpdatedOn: '{{author}} on',
    inactive: 'Inactive',
    workspaceSwitcherInstructions: 'Open / close',
    addFileSuccess: 'Work item file added succesfully',
    deleteWorkItemFile: 'Delete work item file',
    deleteWorkItemFileText: 'Are you sure you want to delete {{fileName}}?',
    startStorageProcess: 'Start process with work items from storages',
    home: 'Home',
    goToHome: 'Go to Home',
    searchWorkspaces: 'Search workspaces...',
    searchOrganizations: 'Search organizations...',
    chooseAssistant: 'Choose assistant',
    readAssistants: 'Read assistants',
    writeAssistants: 'Write assistants',
    readWorkers: 'Read workers',
    writeWorkers: 'Write workers',
    runAssistants: 'Run assistants',
    assistantApi: 'Assistant API',
    listAssistants: 'List assistants',
    listAssistantRuns: 'List runs of an Assistant',
    getAssistant: 'Get assistant',
    assistantId: 'Assistant ID',
    total: 'Total',
    failed: 'Failed',
    succeeded: 'Succeeded',
    pRunFailedWithErrors: 'Completed with errors',
    createWorkItem: 'Create Work Item',
    createWorkItemSuccess: 'Work Item created succesfully',
    startProcessWithNewWorkItemSuccess: 'Process with Work Item started succesfully',
    addNewEntry: 'Add new entry',
    createWorkItemTooltip: 'Create a new work item in the process input',
    environmentGroups: 'Worker Groups',
    environmentGroup: 'Worker Group',
    [EnvGroupTokenExpiration.NEVER]: 'Never',
    [EnvGroupTokenExpiration.IN_A_DAY]: 'In a day',
    [EnvGroupTokenExpiration.IN_A_WEEK]: 'In a week',
    [EnvGroupTokenExpiration.IN_A_MONTH]: 'In a month',
    [EnvGroupTokenExpiration.IN_A_YEAR]: 'In a year',
    createEnvironmentGroup: 'Create worker group',
    environmentGroupName: 'Worker group name',
    deleteEnvironmentGroup: 'Delete Worker Group',
    deleteEnvironmentGroupText: 'Are you sure you want to delete Worker Group {{name}}',
    deletingEnvironmentGroup: 'Deleting Worker Group',
    addEnvironmentToGroup: 'Add this Worker to Worker Group',
    removeEnvironmentFromGroup: 'Remove this Worker from Worker Group',
    includedInGroups: 'Included in Worker Groups',
    organizationTimezoneDescription:
      'Regardless of their location, all users belonging to the organization will see the same date and time.',
    organizationTime: 'Your organization time',
    inYourLocation: 'in your location',
    clock: 'Clock',
    hourClock: 'hour clock',
    localTime: 'Local time (every user will see the date in their local time)',
    nonMemberWorkspaces: 'Non-member workspaces',
    [ErrorSubCodes.ERR_TOO_MANY_WORK_ITEMS]: 'Failed to start run. Too many work items',
    [ErrorSubCodes.ERR_NO_WORK_ITEMS]: 'Failed to start run. No work items',
    numberOfWorkspacesShown: 'Showing {{workspaces}} of {{all}} available workspaces',
    numberOfWorkspacesTooltip:
      'If you are an owner of this organization, you can add yourself as a member.',
    triggerProcessWithEmail: 'Trigger the process with email',
    triggerEmail: 'Trigger email',
    leaveOrganization: 'Leave organization',
    leaveOrganizationText: 'Are you sure you want to leave this organization?',
    leaveOrganizationOwner: 'To leave the organization you need to add a new owner',
    leaveWorkspace: 'Leave workspace',
    leaveWorkspaceText: 'Are you sure you want to leave this workspace?',
    reserveEmailAddress: 'Reserve email address',
    pricingPlanLinkText: 'See our pricing page',
    manyRuntimes: '{{runtimeAmount}} Workers in group',
    noEnvironmentGroups: 'No self-hosted worker groups available.',
    manageEnvironmentGroups: 'Manage available worker groups on the',
    environmentGroupMisconfiguration:
      'Activity has Worker Group set even though they are not enabled. Running the Process is disabled',
    environmentGroupSelectTooltip: 'Run on group of self-hosted workers',
    workItemStatistics: 'Work item statistics',
    yourCurrentPlanIs: 'Your current plan is',
    input: 'Input',
    pending: 'Pending',
    output: 'Output',
    planDowngradeInfo:
      'Downgrading can affect the operation of tasks or creation of new resources. Please refer to plan details.',
    onDemandMinutesDisabled:
      'Purchasing on-demand run minutes is currenty disabled on this tier. Please contact support if you want to purchase more run minutes.',
    onDemandAssistantRunsDisabled:
      'Purchasing on-demand assistant runs is currenty disabled on this tier. Please contact support if you want to purchase more assistant runs.',
    addToSlack: 'Add to Slack',
    ended: 'Ended',
    at: 'at',
    eventsInDuration: '{{ eventCount }} events in {{ duration }} seconds',
    runArtifactsInfo: 'Run artifacts are logs and files that each run generates',
    openLog: 'Open log',
    startup: 'Startup',
    finishing: 'Finishing',
    seconds: 'seconds',
    durationInSeconds: '{{duration}} seconds',
    numberOfEvents: '{{ eventCount }} events in',
    shedulerUpsellInfo: 'Take advantage of recurring triggers and scheduling.',
    shedulerUpsellInfoAction:
      'Upgrade your plan to get access to scheduler, API and e-mail triggers.',
    upsellCallForAction: 'Upgrade your plan now!',
    upsellContactOwner: 'Contact your organization owner to upgrade',
    environmentGroupsUpsellInfo:
      'Group multiple self-hosted workers to take advantage of parallel executions.',
    emailTriggerUpsellInfo: 'Enable triggering processes with e-mails',
    workItemStatus: 'Work Item Status',
    env: 'env.',
    stepsWorkItems: 'Steps and Work Item status',
    showStepDetails: 'Show Step details page',
    upgradePlanToEnableFeat: 'Upgrade your plan to enable this feature',
    hideGraphs: 'Hide graphs',
    showGraphs: 'Show graphs',
    totalValueGenerated: 'Total Value Generated',
    totalValueGeneratedInDays: 'Total Value Generated in Last {{timespan}} Days',
    totalValueGeneratedInBillingPeriod: 'Total Value Generated in Current Billing Period',
    savingsPerWorkItem: 'Savings / Work Item ($)',
    savingsPerWorkItemHelper:
      'Monetary value estimate of how much is saved for each Work Item handled.',
    noValueSet: 'No value set',
    workItemSavingsConfigurationNote: 'Some steps do not have generated value configured.',
    timespanTooltip: 'Select Charts and KPIs timespan',
    noChartDataAvailable: 'No data to plot {{chart}} chart',
    noDataToPlot: 'No data to plot',
    statistics: 'Statistics',
    robotRunMinutes: 'Task Run Minutes',
    robotRunMinutesUsed: 'Robot Run Minutes Used',
    robotRunMinutesUsedInDays: 'Robot Run Minutes Used in Last {{timespan}} Days',
    robotRunMinutesUsedInBillingPeriod: 'Robot Run Minutes Used in Current Billing Period',
    runMinutes: 'Run Minutes',
    change: 'Change',
    valueGenerated: 'Value generated',
    noFiltersFound: 'No filters found',
    statisticsTimespanTooltip: 'Select Statistics Timespan',
    statisticsTimespan: 'Statistics Timespan',
    workDataExplorer: 'Work data explorer',
    openWorkDataExplorer: 'Open work data explorer',
    stepsAndStatus: 'Steps & Status',
    selectAll: 'Select All',
    selectFailed: 'Select Failed',
    retryWorkItem: 'Retry Work Item',
    discardWorkItem: 'Discard Work Item',
    markAsComplete: 'Mark as Complete',
    reloadData: 'Reload data',
    stateUpdated: 'State updated at',
    goToProcessRunDetails: 'Go to Process Run Details Page',
    processRunsDescription:
      'Process runs are completed when all work items have been handled successfully. Process run duration is not counted as billable run minutes; only robot execution time is.',
    yourCurrentPlan: 'Your current plan:',
    thisWorkspace: 'This Workspace',
    otherWorkspaces: 'Other Workspaces',
    allWorkspaces: 'All Workspaces',
    free10: 'Free',
    starter10: 'Starter',
    flex10: 'Flex',
    pro10: 'Pro',
    free20: 'Free',
    starter20: 'Starter',
    flex20: 'Flex',
    pro20: 'Pro',
    free30: 'Developer',
    starter30: 'Starter',
    flex30: 'Flex',
    enterprise30: 'Enterprise',
    noStatistics: 'No Statistics',
    currentBillingPeriod: 'Current Billing Period',
    thisBillingPeriod: 'This Billing Period',
    thisPeriod: 'This period',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    lastWeek: 'Last week',
    lastDays: 'Last {{days}} days',
    lastDay: 'Last days',
    lastHours: 'Last {{hours}} hours',
    allTime: 'All time',
    custom: 'Custom',
    processRunStepsSummaryDescription:
      'Below you can find the steps of this process run and a summary of the work item status. You can explore work items by clicking an element.',
    processRunStepsSummaryHeader: '{{processName}} - Run #{{runNo}}',
    showStepRuns: 'Show step runs...',
    searchRunNumber: 'Search run number...',
    latestWorkData: 'Latest work data',
    processConfiguration: {
      schedule: {
        configuration: {
          triggerFromInputsTitle: 'Run all new work items from input',
          triggerFromInputsDescription:
            'No run will be started if the input is empty. Default work item settings will be ignored.',
        },
      },
    },
    readWebhooks: 'Read webhooks',
    writeWebhooks: 'Write webhooks',
    readSecrets: 'Read secrets',
    writeSecrets: 'Write secrets',
    sendTestEvent: 'Send Test Event',
    writeWorkItems: 'Write work items',
    readAssetStorageAssets: 'Read assets',
    writeAssetStorageAssets: 'Write assets',
    readWorkItems: 'Read work items',
    completeWorkItems: 'Mark Work Items as Done',
    noPermission: 'You do not have permission to perform this action',
    listWorkspaceProcessRuns: 'List workspace process runs',
    onDemandRuntime: 'On Demand Worker',
    onDemandRuntimeName: 'Name',
    onDemandRuntimes: 'On Demand Workers',
    deleteOnDemandRuntime: 'Delete On Demand Worker',
    deleteOnDemandRuntimeText: 'Are you sure you want to delete this on demand worker?',
    noOnDemandRuntimes: 'No self-hosted on-demand workers available.',
    manageOnDemandRuntimes: 'Manage available on-demand workers on the',
    onDemandRuntimeSelectTooltip: 'Run on self-hosted On-Demand workers',
    secret: 'Secret',
    linkTokens: 'Link Tokens',
    environmentGroupLinkTokenInfo:
      'These permanent tokens allow linking worker straight to the worker group. Treat these like passwords, as the connected workers gain access to workspace resources when work gets allocated to them.',
    onboardingSidebar: {
      getStarted: 'Get Started',
      hidePanel: 'Hide this panel',
      showPanel: 'Start',
      closeVideo: 'Back to Control Room',
      intro:
        'Learn the basics of Robocorp products and get started building your first software robot or running them locally.',
      docsTitle: 'Control Room documentation',
      docsIntro: 'Learn how to manage and monitor your automations with Control Room.',
      docsCTA: 'Open Docs',
      videoTitle: 'Learn Robocorp basics',
      videoIntro:
        'Watch the 3 minute intro video to learn the basics of how to build automation with Robocorp.',
      videoCTA: 'Watch Intro video',
      robotTitle: 'Build your first robot',
      robotIntro: 'Our open-source software robot tools are free, powerful, and easy to learn.',
      robotCTA: 'Go to Downloads',
      assistantTitle: 'Install Robocorp Assistant',
      assistantIntro:
        'Robocorp Assistant provides a way to execute attended robots on your computer.',
    },
    workItemsDone: 'Done / Total',
    exceptionType: 'Exception Type',
    exceptionCode: 'Exception Code',
    exceptionMessage: 'Exception Message',
    noCode: 'No Code',
    noMessage: 'No Message',
    processRunHistory: 'Process run history',
    end: 'End',
    stateChanged: 'State changed',
    startRequest: 'Start request',
    stopRequested: 'Stop requested',
    completeRequested: 'Completion requested',
    lastStart: 'Last start',
    lastFinish: 'Last finish',
    nextStart: 'Next start',
    waitingForSlot:
      'Limit for concurrent step runs reached: waiting for the ongoing step runs to finish',
    waitingForSlotLastAttempt: 'Last attempt: ',
    waitingForSlotAttemptNumber: 'Attempt number: {{ attemptNumber }}',
    foundSlot: 'Found slot. Proceeding',
    processRunTimeline: 'Process run timeline',
    timeline: 'Timeline',
    lastAttempt: 'Last attempt: ',
    billingManagedByAccount:
      'The billing information for this organization is managed at the account level.',
    accountManagement: 'Account Management',
    clientOrganizations: 'Client organizations',
    consumption: 'Consumption',
    accountManagementPartnersCallout:
      'Robocorp partners can now manage client organizations and consolidated billing with a Partner Account.',
    becomeAPartner: 'Become a partner',
    becomeRobocorpPartner: 'Become a Robocorp partner',
    applyForPartnership: 'Apply for Partnership',
    partnerAutomationDescription:
      'Robocorp’s partners bring automation to businesses globally, and we proudly set you up for success.',
    partnerProgramDescription:
      'Robocorp’s Partner Program provides access to the technical and training resources to help you grow your automation business.',
    partnerAccount: 'Partner Account',
    partnerAccountOrganizations: '{{accountName}} organizations',
    accountOrganizations: 'Account organizations',
    newClientOrganization: 'New Client Organization',
    valueGeneratedPerWorskpace: 'Total Value Generated per Workspace',
    currentPeriod: 'Current period',
    previousPeriod: 'Previous period',
    currentPeriodWithRange: 'Current period ({{ range }})',
    previousPeriodWithRange: 'Previous period ({{ range }})',
    numberOfProcesses: 'Number of Processes',
    showMore: 'Show more',
    dashboard: 'Dashboard',
    'data analyst': 'Data Analyst',
    openPartnerAccount: 'Manage Partner Account',
    openPartnerAccountDescription:
      'You can now manage multiple client organizations and consolidate billing to one account with Partner Account.',
    otherOrganizations: 'Non-Account Organizations',
    billingInformationOnPartnerAccount: 'Billing information on Partner Account',
    inputErrors: {
      basic: {
        required: '{{ name }} is required',
        min: '{{ name }} must be at least {{ value }}',
        max: '{{ name }} must not exceed {{ value }}',
        minLength: '{{ name }} must contain at least {{ value }} characters',
        maxLength: '{{ name }} must not exceed {{ value }} characters',
        pattern: '{{ name }} is not valid',
        step: '{{ name }} accepts increment {{ value }}',
      },
      activityDurationLimit:
        'Execution timeout is restricted to {{ value }} minutes by current billing plan',
      concurrencyLimit: 'Parallel executions are restricted to {{ value }} by current billing plan',
    },
    automaticDeployEnabled: 'Deploy incoming changes automatically',
    commitDeployed: 'Commit deployed',
    nothingDeployedBranchNotFound: 'Nothing has been deployed. Branch not found.',
    newChangesAvailable: 'Update available',
    notUploaded: 'No commit information found',
    branch: 'Branch',
    updating: 'Updating',
    sessionGuardMessage: 'Your session has expired',
    sessionGuardDescription: 'Please refresh the page to continue.',
    sessionGuardAction: 'Refresh',
    processesUsedInWorkspace: '{{ used }}/{{ limit }} Processes',
    upgradeInfo: {
      default: 'Upgrade to get more out of your subscription.',
      outputWorkItemLimit:
        'Output Work Items limited to {{ limit }}. If more resources are required upgrade current plan.',
      organizationShortNameRestriction:
        'Editing organization short name is restricted. To use this feature upgrade current plan.',
      stepRunConcurrencyLimit:
        'Step run parallel executions are restricted to {{ limit }}. If more resources are required upgrade current plan.',
      stepRunDurationLimit:
        'Step run duration is limited to {{ limitInMinutes }} minutes ({{ limit }} seconds). If more resources are required upgrade current plan.',
    },

    planMinutesUsed: 'Plan minutes used',
    planRunsUsed: 'Plan runs used',
    onDemandMinutesUsed: 'On-demand minutes used',
    onDemandRunsUsed: 'On-demand runs used',
    onDemandRunMinutesInfo:
      'After your automations have consumed the minutes included in your monthly plan, you can ensure the automations keep running with on-demand minutes. These are charged by a per minute fee.',
    onDemandRunsInfo:
      'After your assistants have consumed the runs included in your monthly plan, you can ensure the assistants keep running with on-demand runs. These are charged by a per run fee.',
    onDemandMinutesCost: 'On-demand minutes cost',
    onDemandRunsCost: 'On-demand runs cost',
    perMinute: '{{ amount }}/minute',
    perRun: '{{ amount }}/run',
    perMonth: '/month',
    amountOfMins: '{{ amount }} mins',
    limitOnDemandMinutes: 'Limit on-demand run minutes',
    limitOnDemandRuns: 'Limit on-demand assistant runs',
    onDemandMinutesDisabledP1: `You won’t be able to run tasks after the run minutes included in the plan have been used.`,
    onDemandMinutesDisabledP2: `Enable on-demand run minutes to keep tasks running after reaching the plan limit.`,
    onDemandRunsDisabledP1: `You won’t be able to run assistants after the runs included in the plan have been used.`,
    onDemandRunsDisabledP2: `Enable on-demand assistant runs to keep assistants running after reaching the plan limit.`,
    onDemandMinutesExceededP1: `All of your run minutes have been used.`,
    onDemandMinutesExceededP2: `Ongoing process runs will finish, but new runs won’t be able to start until you upgrade your tier.`,
    onDemandRunsExceededP1: `All of your assistant runs have been used.`,
    onDemandRunsExceededP2: `Ongoing assistant runs will finish, but new runs won’t be able to start until you upgrade your tier.`,
    unlimitedRunMinutesEnabled: 'Your plan includes unlimited run minutes.',
    on: 'On',
    off: 'Off',
    noLimit: 'no limit',
    warning: 'Warning',
    info: 'Info',
    nextConfigLimitError: "New limit can't be less than current usage",
    amountOfMinutesUsed: '{{ amount }}/{{ of }} minutes used',
    amountOfRunsUsed: '{{ amount }}/{{ of }} assistant runs used',
    toggleNewNavigation: 'Toggle new navigation',
    toggleNewNavigationDescription:
      'Enable to take the new version of Control Room’s navigation into use. Note that the new navigation is still in early stage development, so there might still be some rough edges.',
    selectRepository: 'Select repository',
    selectRepositoryBefore: 'Select the repository before selecting the branch',
    connect: 'Connect',
    reconnect: 'Reconnect',
    robotNotLinkedToRepository: 'The robot is not linked to any repository',
    gitlabUrlInfo: 'Your self hosted Gitlab URL. e.g. https://gitlab.yourcustomdomain.dom',
    gitlabClientIdInfo: 'The client ID from your Gitlab Application',
    gitlabClientSecretInfo: 'The client secret from your Gitlab Application',
    robotHasBeenDisconnectedGitlab: 'Robot has been disconnected from the Gitlab',
    robotHasBeenDisconnectedGithub: 'Robot has been disconnected from the Github',
    disconnect: 'Disconnect',
    installed: 'Installed',
    organizationIntegrations: {
      description:
        'Connect your Organization with a third-party service to deploy task packages from private repositories.',
      connectGithub: 'Connect Github',
      connectGitlab: 'Connect Gitlab',
      manageIntegration: 'Manage integration',
      addNewInstallation: 'Add new installation',

      disconnectGithubP1: 'You are about to remove the link to',
      disconnectGithubP2: 'from this organization.',
      disconnectGithubP3:
        'All task packages that were linked to repositories from this organization will stop receiving updates',
      disconnectGithubP4:
        'Repositories available by this installation won’t be accessible when creating a new task packages',

      disconnectGitlabP1: 'You are about to remove the link to',
      disconnectGitlabP2: 'from this organization.',
      disconnectGitlabP3:
        'All task packages that were linked to repositories accessible to this user will stop receiving updates',
      disconnectGitlabP4:
        'Repositories available by this installation won’t be accessible when creating a new task package',

      disconnectGitP2:
        'This integration is used by {{ count }} task packages which are going to be unlinked from their repositories and won’t update on new code pushes.',
      organizationIntegrationSettings: 'organization integration settings',
      editPermissions: 'You can edit the permissions in the',
      gitlabDisabled: 'Each organization can be linked to a single GitLab user',
      selfHostedGitlab: 'Self Hosted Gitlab',
      gitIntegrationsLocked: 'Git integrations are temporarily unavailable, come back later',
      githubInstallationSuccess: 'GitHub App installed succesfully!',
      gitlabInstallationSuccess: 'GitHub App installed succesfully!',
      returnToCr: 'Return to Control Room',
      chooseOrganization: 'Choose organization',
      githubAlreadyInstalled:
        'Following organizations already have the Github Robocorp Application installed',
      githubAlreadyInstalledP:
        'Pick which Github organizations should be linked to this Control Room organization or add a new installation',
      gitlabReconnectText:
        'Looks like GitLab access has been revoked for the Control Room app. Please reconnect your GitLab account.',
      onlyAdminCanManage: 'Git integrations can be managed only by the organization administrator',
      branchDeleted: 'Branch "{{ branch }}" has been deleted',
      changeBranch: 'Change branch',
      noRepositoriesFound: 'No repositories found',
      githubLinkExistingError: 'Failed to link existing Github installation',
      [EmailConfigurationErrors.GET_EMAIL_CONFIGURATION_ERROR]:
        'Failed to retrieve the email configuration',
      [EmailConfigurationErrors.UPSERT_EMAIL_CONFIGURATION_ERROR]:
        'Failed to create or update the email configuration',
      [EmailConfigurationErrors.DELETE_EMAIL_CONFIGURATION_ERROR]:
        'Failed to delete the email configuration',
      smtpIntegration: {
        description: 'Use your own SMTP server to send outgoing Control Room emails',
        button: {
          create: 'Configure your SMTP server',
          update: 'Update your SMTP server configuration',
          tooltip: 'Only organization owners can modify the SMTP configuration',
        },
        actions: {
          create: 'Create',
          update: 'Update',
          delete: 'Delete',
          confirm: {
            title: 'Delete the SMTP configuration',
            description: 'Are you sure you want to delete the SMTP configuration?',
          },
        },
        indicator: {
          valid: 'Valid configuration: Control Room emails are sent using this configuration',
          invalid: 'Invalid configuration: Control Room could not authenticate to the SMTP server',
        },
        title: {
          header: 'Email SMTP Configuration',
          create: 'Configure your SMTP server',
          update: 'Update your SMTP server configuration',
        },
        configuration: {
          title: 'Server configuration',
          description: 'Configure your SMTP server below',
          host: {
            label: 'Host',
            placeholder: 'Host',
          },
          port: {
            label: 'Port',
            placeholder: 'Port',
          },
          authentication: {
            username: {
              label: 'Username',
              placeholder: 'Username',
            },
            password: {
              label: 'Password',
              placeholder: 'Password',
            },
          },
          from: {
            title: 'Sender configuration',
            description: 'The emails will be sent by {{sender}}',
            name: {
              label: 'Name',
              placeholder: 'Sender name',
            },
            address: {
              label: 'Address',
              placeholder: 'Sender address',
            },
          },
        },
      },
    },
    pause: 'Pause',
    paused: 'Paused',
    activate: 'Activate',
    webhooksEvents: {
      [OutgoingEventType.ASSISTANT_RUN]: 'Assistant run',
      [OutgoingEventType.PROCESS_RUN]: 'Process run',
      [OutgoingEventType.STEP_RUN]: 'Step run',
      [OutgoingEventType.ROBOT_RUN_AGENT]: 'Task run agent',
      [ProcessWebhookEventType.PROCESS_RUN_STARTED]: 'process_run.started',
      [ProcessWebhookEventType.PROCESS_RUN_COMPLETED]: 'process_run.completed',
      [ProcessWebhookEventType.PROCESS_RUN_UNRESOLVED]: 'process_run.unresolved',
      test_event: 'event.test',
    },
    webhookTypes: {
      [WebhookType.WORKSPACE_WEBHOOK]: 'Workspace webhooks',
      [WebhookType.PROCESS_WEBHOOK]: 'Process webhooks',
    },
    all: 'All',
    none: 'None',
    [ErrorActions.WebhookCreateError]: 'Webhook Creation Error',
    [ErrorActions.WebhookEditError]: 'Webhook Editing Error',
    deleteAllArtifacts: 'Delete all',
    deleteAllArtifactsText: 'Are you sure you want to delete all artifacts of this run?',
    deleteArtifact: 'Delete Artifact',
    deleteArtifactText: 'Are you sure you want to delete artifact "{{ fileName }}?"',
    organizationInvitedDescription: 'You have been invited to join an existing organization.',
    organizationInviteDescription: 'Join organization or create your own to get started.',
    signedInLabel: 'Signed in as',
    switchAccount: 'Switch Account',
    noEntries: 'No entries',
    search: 'Search',
    searchClear: 'Clear Search',
    period: 'Period',
    systemService: 'System service',
    action: 'Action',
    actor: 'Actor',
    actors: 'Actors',
    noItemsFound: 'No items found',
    asc: 'Ascending',
    desc: 'Descending',
    releaseNotesTitle: 'Latest Updates from Robocorp',
    linking: {
      linkAccount: 'Link Account',
      useDifferentAccount: 'Use a different account',
      troubleshoot:
        'Refer to <1>manual linking guide</1> or <3>contact us</3><br/> if you have trouble linking.',
      descriptions: {
        'Robocorp Automation Studio': 'Link your Control Room account with Automation Studio.',
        'Robocorp Assistant':
          'Link your Control Room account to run software tasks locally on your computer.',
        'Robocorp Agent': 'Link your Control Room account with Workforce Agent',
      },
    },
    resetFilters: 'Reset filters',
    auditLogs: {
      new: 'New audit logs',
      deprecationNoticeP1: `We're excited to introduce the new and improved version of Audit logs.`,
      deprecationNoticeP2: `The past records won't be transferred to the new Audit logs, so back up if needed.`,
      revertToOldInterface: 'Back to the old version',
      previewNewAuditLogs: 'Check it out',
      actionType: 'Action type',
      resourceType: 'Resource type',
      resourceName: 'Resource name',
      oldValue: 'Old value',
      newValue: 'New value',
      clickForDetails: 'Click to see update details',
      reason: 'Reason',
      updateDetails: {
        planId: {
          dev1: 'Developer',
          team1: 'Team',
          pro1: 'Pro (legacy)',
          free1: 'Free',
          free10: 'Free',
          free20: 'Free',
          basic1: 'Basic',
          pro2: 'Pro',
          pro10: 'Pro',
          pro20: 'Pro',
          starter10: 'Starter',
          starter20: 'Starter',
          flex10: 'Flex',
          flex20: 'Flex',
          enterprise: 'Enterprise',
          'partner-demo10': 'Partner Demo',
          'partner-starter10': 'Partner Starter',
          'partner-pro10': 'Partner Pro',
        },
      },
      userIpAddress: 'IP address: {{ userIp }}',
    },
    downloadCsv: 'Download CSV',
    downloadLastExport: 'Download last export',
    creatingCsv: 'Creating CSV export',
    progress: 'Progress',
    robocorpAdmin: 'Robocorp Admin',
    downloadStatus: {
      IN_PROGRESS: 'In progress',
      FAILED: 'Export failed',
      SUCCEEDED: 'Success',
    },
    nameContainsUrl: `Name can't contain URL`,
    invalidConfig: 'Invalid process configuration',
    invalidConfigDescription: 'Please review the provided configuration and try again',
    back: 'Back',
    importProcess: 'Import process',
    inputWorkItems: 'Input Work Items',
    viewProcess: 'View process',
    workers: {
      instanceSizeMultiplier: 'Worker resource configuration',
      instanceSizeMultiplierDescription:
        'Adjust the resources allocated for this Cloud worker. The selected multiplier will apply to billed run minutes.',
      autoUpdateCurrently: 'currently: {{version}}',
      deprecatedMarker: 'deprecated',
      details: 'Details',
      availability: 'Availability',
      noReasonSpecified: 'No reason specified',
      reason: 'Reason',
      textWarningOnEnabling:
        'Enabling the worker will resume ongoing step runs. New work will be allocated to this worker.',
      textWarningOnDisabiling:
        'The ongoing step runs will be allowed to progress as usual. New work will not be allocated to this worker while it remains disabled.',
      confirmTitle: 'Are you sure?',
      update: 'Update',
      disable: 'Disable',
      enable: 'Enable',
      cancel: 'Cancel',
      cloud: 'Cloud Environment',
      title: 'Workers',
      singular: 'Worker',
      description:
        'A Worker is a computing resource, Self-Hosted or in Cloud, that executes the configured Task when the Step Run is triggered.',
      summary: 'Description',
      level: 'Level',
      severity: 'Severity',
      event: 'Event',
      plural: 'Workers',
      notifications: 'Notifications',
      selfHostedWorkers: 'Self-hosted Workers',
      selfHostedWorkersDescription:
        'Self-hosted Workers allow you to run tasks on your own machines.',
      groups: 'Groups',
      eventlog: 'Event Log',
      deleteWorkerText: 'Do you want to delete "{{ name }}" worker?',
      deleteBulkText: 'Do you want to delete worker?',
      deleteBulkText_plural: 'Do you want to delete {{ count }} workers?',
      availabilityText: 'Do you want to {{ action }} "{{ name }}" worker?',
      availabilityBulkText: 'Do you want to {{ action }} worker?',
      availabilityBulkText_plural: 'Do you want to {{ action }} {{ count }} workers?',
      deleteGroupText: 'Do you want to delete "{{ name }}" worker group?',
      deleteGroupBulkText: 'Do you want to delete worker groups?',
      deleteGroupBulkText_plural: 'Do you want to delete {{ count }} worker groups?',
      deleteOnDemandText: 'Do you want to delete "{{ name }}" on-demand worker?',
      deleteOnDemandBulkText: 'Do you want to delete on-demand worker?',
      deleteOnDemandBulkText_plural: 'Do you want to delete {{ count }} on-demand workers?',
      linkWorker: {
        title: 'New self-hosted worker',
        newWorker: 'New worker',
        description: 'Self-hosted Workers allow you to run tasks on your own machines.',
        explanationTitle: 'Find the correct setup for you',
        explanationDscriptio:
          'To get started, let’s find a Worker configuration that best suits your use case.',
        learnMoreTitle: 'Find the correct setup for you',
        learnMoreSubtitle:
          'To get started, let’s find a Worker configuration that best suits your use case.',
        learnMoreDescription:
          'The Robocorp platform supports different types of hosting configurations for Workers, which are suitable for different use cases.',
        missingPermissionDescription:
          'You do not have the necessary permissions to create a worker.',
      },
      group: {
        title: 'Worker Group',
        plural: 'Worker Groups',
        newGroup: 'New Group',
        newTitle: 'New Worker Group',
        configureTitle: 'Configure Worker Group',
        description:
          'Worker groups provide a way to assign multiple self-hosted workers into named groups and take advantage of concurrency.',
        groupName: 'Group Name',
        groupNameDescription:
          'Short and unique names will catch your attention better and help organizing your groups.',
        lastStateUpdated: 'Last state updated',
        addWorkersEmptyTitle: 'Add Self-hosted Workers',
        addWorkersEmptyDescription:
          'Add self-hosted workers to the group and take advantage of concurrency.',
        addWorkers: 'Add Workers',
        addWorkersDescription: 'Select workers you want to add to the group',
        addWorkerAction: 'Add {{count}} worker',
        addWorkerAction_plural: 'Add {{count}} workers',
        groupCreatedSuccess: 'Worker Group has been created succesfully',
        removeWorker: 'Remove',
        removeWorkerTitle: 'Remove Worker from Group',
        removeWorkerConfirmation: 'Are you sure you want to remove Worker from this Worker Group?',
        deleteWorkerGroup: 'Delete Worker Group',
        deleteWorkerGroupConfirmation: 'Are you sure you want to delete this Worker Group?',
      },
      onDemand: {
        title: 'On-demand Workers',
        newWorker: 'New On-demand Worker',
        configureWorker: 'Configure On-demand Worker',
        description:
          'On-demand Worker webhooks allow you to execute task packages on a custom, self-hosted infrastructure.',
        webhookName: 'Webhook Name',
        webhookDescription: 'Short and unique names will catch your attention better.',
        workerCreatedSuccess: 'On-demand worker has been created succesfully',
        workerConfigureSuccess: 'On-demand worker has been updated succesfully',
        name: 'On-demand Workers Name',
        transportType: 'Transport Type',
        transportTypeDescription: 'Select one of the transport options.',
        webhookType: {
          title: 'Webhook',
          description:
            'Control Room requests a new worker to be started via a HTTPS request. Requires the endpoint to be exposed on Internet.',
        },
        eventBridgeType: {
          title: 'AWS Event Bridge',
          description:
            'Control Room requests new worker to be started by sending a message via AWS EventBridge. Learn more link to https://robocorp.com/docs/control-room/aws-integrations/eventbridge.',
          developmentMode: 'Development mode',
          developmentModeDescription:
            'In development mode the additional details including the worker link token are sent as part of the message without application-layer encryption. This can be used to develop and test the system and decryption implementation.',
        },
      },
      agent: {
        title: 'Workforce Agent',
        description:
          'Install the Workforce Agent application to use your self-hosted machine for running tasks with the Control Room. Please see the documentation for further information and usage.',
        download: 'Download Workforce Agent',
        installationGuide: 'View installation guide',
      },
      setupUtility: {
        title: 'Setup Utility',
        description:
          'Install the Setup Utility application to use your self-hosted machine for running tasks with the Control Room. Please see the documentation for further information and usage.',
        download: 'Download Setup Utility',
        installationGuide: 'View installation guide',
      },
      linkTokens: {
        title: 'Group Tokens',
        description: 'Use a group token to link multiple workers into the worker group.',
        tokenName: 'Token Name',
        tokenValue: 'Token value',
        copyToken: 'Copy Token',
        displayToken: 'Display Token',
        removeToken: 'Remove Token',
        addNewToken: 'Add more',
        timeout: 'Timeout in minutes',
        requiresSave: 'The new token will be generated once you update the worker group',
        autoremovalCheckbox: 'Remove inactive workers automatically',
        autoremovalCheckboxDescription:
          'Configure the amount of time after which an OFFLINE worker will be automatically deleted. Use this option with on-demand workers that do not have a persistent storage.',
      },
      workerAutoremovalCheckbox: 'Automatically remove when OFFLINE for a period of time',
      workerAutoremovalCheckboxDescription:
        'Set a timeout after which the worker will be deleted when inactive. Use this option with containerized workers that do not have a persistent storage.',
      onDemandGuide: 'Follow the guide to create your first on demand worker',
      create: 'Create worker',
      createGroup: 'Add worker group',
      hostname: 'Host name',
      online: 'Online',
      offline: 'Offline',
      agentVersion: 'Agent version',
      onDemandRuntimeDescriptionP1:
        'To use On-demand Workers, an On-demand Worker Provisioner is needed.',
      onDemandRuntimeDescriptionP2:
        'The provisioner is a service that runs on local infrastructure, listens for requests for new workers from Control Room, authenticates those requests, and starts and stops workers as requested.',
      groupsGuide: 'Follow the guide to create your first worker group',
      download: 'Download Workforce Agent App',
      lastRun: 'Last run',
      name: 'Worker name',
      groupName: 'Worker group name',
      delete: 'Delete worker',
      deleteText: 'Are you sure you want to delete this worker?',
      platform: 'Platform',
      architecture: 'Architecture',
      release: 'Release',
      ip: 'IP address',
      configure: 'Configure',
      groupRuntimes: 'Group workers',
      notAdded: 'Workers not added',
      notAvailable: 'Workers not available',
      addLinkToken: 'Add link token',
      cloudDescription: 'Fully managed cloud worker offered by Robocorp.',
      type: 'Type',
      viewWorker: 'View Worker',
      viewWorkerGroup: 'View Group',
      cloudWorker: 'Cloud Worker',
      lastStateUpdate: 'Last State Update',
      hostResources: {
        title: 'Host resources',
        cpu: 'CPU',
        memory: 'Memory',
        storage: 'Storage',
        statsLastHours: 'Last {{ hours }} hour data',
      },
      notificationEventTypes: {
        offline: 'Offline',
        error: 'Error',
      },
      notified: 'Notified',
      notificationPreferences: {
        title: 'Configure Worker Notifications',
        description: 'Get notified about error or offline events of workers',
      },
      emailNotifications: {
        title: 'Email notifications',
        description: 'Get notified about error or offline events of workers.',
        removeEmail: 'Remove Email',
      },
    },
    assistantPermissions: {
      permissions: 'Permissions',
      readSecrets: 'Read Vault Secrets',
      writeSecrets: 'Write Vault Secrets',
    },
    assistantNotifications: {
      emailNotifications: 'Email Notifications for {{ assistantName }}',
      slackNotifications: 'Slack Notifications for {{ assistantName }}',
      failedAssistantRuns: 'Failed Assistant Runs',
      noSlackChannels: 'No Slack channels connected.',
    },
    workItem: {
      singular: 'Work item',
      stepLog: 'Step log',
      advancedSettings: 'Advanced details',
      additionalInformation: 'Additional Information',
      hideAdvancedSettings: 'Hide advanced details',
      retry: 'Retry',
      run: 'Run',
      saveAndRetry: 'Save & Retry',
      markAsDone: 'Mark as Done',
      deleteData: 'Delete work item data',
      delete: 'Delete work item',
      addFiles: 'Add files',
      runLogs: 'Run logs',
      eventLog: {
        title: 'Work Item Log',
        show: 'Show log',
        timestamp: 'Timestamp',
        action: 'Action',
        actor: 'Actor',
        step: 'Step',
      },
    },
    dataExplorer: {
      sortBy: {
        label: 'Sort by...',
        createTs: 'Created At',
        stateTs: 'Updated At',
        state: 'State',
      },
      itemsSelected: `{{count}} selected`,
      deselect: 'Deselect',
      confirmRun: {
        description: 'Selected work item will be run.',
        description_plural: 'Selected new {{ count }} work items will be run.',
        description_all: 'All viable work items will be run.',
        action: 'Run',
      },
      confirmRetry: {
        description: 'Selected work item will be retried.',
        description_plural: 'Selected failed {{ count }} work items will be retried.',
        description_all: 'Please note that all items matching the filters below will be retried.',
        action: 'Retry',
      },
      confirmDiscard: {
        description:
          "Selected work item will be marked as done. This work item can't be retried once this action has been performed.",
        description_plural:
          "Selected failed {{ count }} work items will be marked as done. These work items can't be retried once this action has been performed.",
        description_all:
          'Please note that all items matching the filters below will be marked as done.',
        action: 'Mark as Done',
      },
      confirmDelete: {
        description:
          "Selected work item will be deleted. This work item can't be retried once this action has been performed.",
        description_plural:
          "Selected {{ count }} work items will be deleted. These work items can't be retried once this action has been performed.",
        description_all: 'Please note that all items matching the filters below will be deleted.',
        action: 'Delete',
      },
    },
    onboarding: {
      title: 'Onboarding',
      gettingStarted: 'Getting started',
      welcome: {
        title: 'Welcome to Control Room',
        description:
          'From implementing your first task to scaling up with advanced use cases, Robocorp supports you on your automation journey. Check out our essential resources below.',
        quickStartTitle: 'Quickstart Guide and Documentation',
        quickStartDescription: 'From beginner to pro - learn Robocorp.',
        VSCodeExtensionTitle: 'Install Robocorp extension for VS Code',
        VSCodeExtensionDescription: 'Bring all the Robocorp features into Visual Studio Code.',
        examplesTitle: 'Portal and Examples',
      },
      helpMenuInfo: 'All resources still available here.',
    },
    processDocumentation: {
      documentation: 'Documentation',
      addDescription: 'Add description',
      noDescription:
        'No documentation for this process yet. Try adding a description here, it supports markdown.',
    },
    processOnboarding: {
      getStartedTitle: "Let's get started",
      getStartedSubtitle:
        "Discover the ease of automation with Robocorp! Start by creating a new process from the examples below or build your own from scratch. We're here to support you at every step.",
      onboarding: '👀 Explore process',
      title:
        'We created this demo process for you to get started. You can explore the process configuration and dive into details and documentation.',
      learnMore: 'Learn more',
      uptimeMonitoring: 'Uptime monitoring',
      uptimeMonitoringDescription:
        'Check the availability of a website and notifying you if it is down.',
      webStoreOrderProcessor: 'Web store order processor',
      webStoreOrderProcessorDescription:
        'Create an example list of items to purchase from the Robocorp swag store using a vault with necessary credentials.',
      startFromScratch: 'Start from scratch',
      startFromScratchDescription:
        'Create a new process with your own settings to automate any use case.',
      goToTaskPackage: 'Go to task package',
      goToSteps: 'Go to steps',
      goToNotifications: 'Go to notifications',
      goToSchedule: 'Go to schedule',
      goToAsset: 'Go to asset',
      goToVault: 'Go to vault',
      timelineCreateProcessLabel: 'Creating the process',
      timelineUploadTaskPackageLabel: 'Uploading the task package',
      timelineUploadTaskPackageDescription:
        'The code for any automation is stored in a task package.',
      timelineConfigureStepsLabel: 'Configuring the steps',
      timelineConfigureStepsDescription: {
        [OnboardingProcessTypes.UPTIME_MONITORING]:
          'An automation can have many steps. This example has two: the first one retrieves the list of websites to monitor, the second one monitors them.',
        [OnboardingProcessTypes.WEB_STORE_ORDER_PROCESSOR]:
          'An automation can have many steps. This example has two: the first one splits the orders from the file in asset storage, the second one processes them.',
      },
      timelineConfigureNotificationsLabel: 'Configuring notifications',
      timelineConfigureNotificationsDescription:
        'You can choose to receive notifications like an email or a Slack message about the state of your automation.',
      timelineConfigureScheduleLabel: 'Configuring the schedule',
      timelineConfigureScheduleDescription:
        'Schedule your automation to run automatically at a certain time or interval. You can also trigger it manually.',
      timelineConfigureAssetsLabel: 'Configuring the asset',
      timelineConfigureAssetsDescription:
        'If your automation needs to read PDFs, CSVs, images or other files, store them in the asset storage.',
      timelineConfigureVaultLabel: 'Configuring the vault',
      timelineConfigureVaultDescription:
        'Securely store your credentials and other sensitive data in the Vault. You can then use them in your automations.',
      timelineRunningAutomationLabel: 'Running automation',
      onboardingCompletedTitle: 'The process has been created!',
      onboardingCompletedDescription:
        "Now, let's dig into the setup and features in the Control Room that we used for this.",
      onboardingCompeltedExploreConfig: 'Got it',
    },
    helpMenu: {
      supportTicket: 'Create a support ticket',
      documentation: 'Documentation',
      portal: 'Portal',
      releaseNotes: "What's new",
      feedback: 'Share feedback',
      downloads: 'Downloads',
      quickStart: 'Quickstart Guide',
    },
    toggleMainMenu: 'Toggle main menu',
    assistantUserImport: {
      assistantOnlyUser: 'Assistant only',
      header: 'Assistant Only User Import',
      description:
        'Bulk create assistant only users and their access credentials based on the input CSV file.',
      exampleP1: 'The input CSV should follow these requirements:',
      exampleReq5: 'Use <0>utf-8</0> text encoding',
      exampleReq10: 'Have the following columns: ',
      exampleReq20: 'Use <0>;</0> as a delimiter',
      exampleReq30: ' should be between 1 and 255 characters long',
      exampleReq40: ' should be between 1 and 255 characters long',
      exampleReq50: ' should be a valid email',
      exampleReq60: 'maximum of 500 rows',
      exampleDownload: 'Download example CSV',
      output:
        'The example here meets all the requirements. After successful upload we will return a similiar CSV with credentials added to each user row.',
      upload: 'Upload CSV',
      errorsHeader: 'CSV file failed parsing',
      errorsDescription: 'Please fix the following issues and try again.',
      errorsHeaderEmpty: 'Empty CSV file',
      errorsHeaderEmptyDescription:
        'Could not parse any valid user rows from the CSV. Please check the file and try again.',
      errorsBackendSkipped: 'Following {{ count }} users could not be created:',
      errorsBackendSkippedCTA: 'Should the problem persist, contact robocorp',
      creationDescriptionP1:
        'You are about to create {{ count }} new assistant only users. This will create a CSV file with access credentials to all of these users. While these users will only have limited permissions, the credentials should still be treated with care.',
      creationDescriptionP2: 'Click "Submit" to proceed.',
      success: 'Successfully imported {{ count }} users.',
      rowNumber: 'Row number',
      download: 'Download CSV with user credentials',
    },
    stepRun: {
      title: 'Step Run',
      plural: 'Step Runs',
      run: 'Run',
      totalWorkItems: 'Total work items',
      robot: 'Task',
      worker: 'Worker',
      workerGroup: 'Worker Group',
      environment: 'Environment',
      runStart: 'Run start',
      runEnd: 'Run end',
      duration: 'Duration',
      console: {
        title: 'Console output',
        titleWithStream: 'Console & Stream',
        view: 'View',
        vertical: 'Vertical',
        horizontal: 'Horizontal',
      },
      artifacts: 'Artifacts',
      timeline: 'Timeline',
      videoStream: 'Video stream',
      downloadArtifact: 'Download Artifact',
      artifactPreview: 'Artifact preview',
      totalWorkItemsShort: 'Total WI',
      succesfullWorkItemsShort: 'Done WI',
      failedWorkItemsShort: 'Failed WI',
      showAllFiles: 'Show All Files',
      stopRun: 'Stop run',
      deleteRun: 'Delete run',
      status: 'Status',
      result: 'Result',
      startTime: 'Start time',
      createTime: 'Create time',
      endTime: 'End time',
      outputs: 'Outputs',
      exceptions: 'Exceptions',
      choose: 'Choose step run',
    },
    [SsoManagementQueryErrors.RBAC_CREATE_RULE_ERROR]: 'Failed to create the SSO group',
    [SsoManagementQueryErrors.RBAC_DELETE_RULE_ERROR]: 'Failed to delete the SSO group',
    [SsoManagementQueryErrors.RBAC_EDIT_RULE_ERROR]: 'Failed to edit the SSO group',
    [SsoManagementQueryErrors.RBAC_GET_ORGANIZATIONS_ERROR]: 'Failed to get SSO configuration',
    [SsoManagementQueryErrors.RBAC_GET_RULES_ERROR]: 'Failed to retrieve the SSO groups',
    [SsoManagementQueryErrors.RBAC_CREATE_WORKSPACE_ERROR]: 'Failed to create the workspace',
    ssoManagement: {
      title: 'SSO Management',
      missingPermission: 'You do not have the necessary rights to access this page',
      missingPermissionCTA: 'If you believe that this is an error, contact our support.',
      addGroup: 'Add group',
      editGroup: 'Edit group',
      deleteGroup: 'Delete SSO group',
      deleteGroupConfirmation: 'Are you sure you want to delete the group {{ruleName}}?',
      deleteGroupDetails:
        'The members of this group will lose the organisation and workspace(s) access tied to this group',
      groupName: 'Group name',
      groupNameHint: `We recommend choosing a name that best describe the role. Spaces are not allowed: use underscores ("_") and dashes ("-") instead: ROBOCORP_MY-WORKSPACE_ADMIN`,
      accessRules: 'Access Rules',
      accessRulesDescription:
        'Select the organization and workspaces you want to include for this group as well as the related roles.',
      organization: 'Organization',
      workspace: 'Workspace',
      role: 'Role',
      selectOrganization: 'Select an organization',
      selectOrganizationForWorkspace: 'Select the organization for the workspace',
      selectWorkspace: 'Select a workspace',
      createWorkspace: 'Create workspace',
      existingWorkspaces: 'Existing workspaces',
      workspaceToCreate: 'Workspace to create',
      nextSteps: 'Next steps',
      nextStepsDescription:
        'Once you have created the workspace, you will be able to select this workspace when adding and updating existing SSO groups.',
      selectRole: 'Select a role',
      addWorkspace: 'Add workspace',
      cancel: 'Cancel',
      createGroup: 'Create',
      updateGroup: 'Update',
      groupError: 'Please give a name to your group',
      organizationError: 'Please select an organization',
      workspaceError: 'Please select a workspace',
      roleError: 'Please select a role',
      createAgentWorkspace: 'Agent workspace',
    },
    groups: 'Groups',
    attended: 'Attended',
    unattended: 'Unattended',
    organizationId: 'Organization ID',
    wsOperationsForOrganizationAdmin: 'Administrative operations',
    wsOperationsForOrganizationAdminDescription:
      'Operations available as <strong>{{orgRoleName}}</strong> of organization <strong>{{orgName}}</strong>',
    promoteSelfToWsAdmin: 'Promote yourself to workspace admin',
    ownRoleInWorkspace: 'Own role in workspace',
    powered: 'Powered by Robocorp',
    filter: {
      title: 'Filter',
      select: 'Select',
      deselect: 'Deselect',
      selected: `{{count}} selected`,
      selectAll: `Select all {{ count }}`,
      reset: 'Reset Filters',
      resultHint: 'Showing {{count}} out of {{total}} {{label}}.',
      period: 'Period',
      periodTimespan: 'Period Timespan',
      all: 'All',
      loadMore: 'Load more',
      items: 'Items',
      sort: {
        by: 'Sort by',
        order: 'Sort order',
        asc: 'Ascending',
        desc: 'Descending',
      },
      setting: {
        rows: 'Rows',
        visibility: 'Columns',
      },
      element: {
        createTs: 'Created At',
        stateTs: 'Updated At',
        state: 'State',
      },
      completeRun: {
        markItems: 'Complete {{count}} run',
        markItems_plural: 'Complete {{count}} runs',
        markItemsDialogText1: 'You are about to mark all Work Items in a Run as Done.',
        markItemsDialogText1_plural:
          'You are about to mark all Work Items in {{count}} Runs as Done.',
        markItemsDialogText2:
          'This will complete the process runs, but might cause loss of information about failures.',
        reason: 'Reason for marking the Work Items as Done',
      },
      deleteRun: {
        markItems: 'Delete {{count}} run',
        markItems_plural: 'Delete {{count}} runs',
        markItemsDialogText1: 'You are about to delete a run and related Work Items.',
        markItemsDialogText1_plural:
          'You are about to delete {{count}} runs and related Work Items.',
      },
    },
    process: {
      documentation: 'Documentation',
      title: 'Process',
      plural: 'Processes',
      name: 'Name',
      run: 'Run Process',
      configure: 'Configure',
      nextRun: 'The next run is scheduled on <timestamp/>',
      totalWorkItems: 'Total work items',
      totalWorkItemsShort: 'Total WI',
      doneWorkItemsShort: 'Done WI',
      failedWorkItems: 'Failed work items',
      inputs: 'Inputs',
      outputs: 'Outputs',
      exceptions: 'Exceptions',
      noExceptions: 'No exceptions',
      runMinutes: 'Run Minutes',
      previousRunMinutes: 'Previous Run Minutes',
      runMinutesChange: 'Run Minutes Change',
      valueGenerated: 'Value Generated',
      previousValueGenerated: 'Previous Value Generated',
      valueGeneratedChange: 'Value Generated Change',
      failedWorkItemsShort: 'Failed WI',
      previousFailedWorkItemsShort: 'Previous Failed WI',
      failedWorkItemsShortChange: 'Failed WI Change',
      createTs: 'Created at',
      lastRun: 'Last run',
      lastFinish: 'Last finish',
      nextStart: 'Next start',
      delete: 'Delete process',
      deletedPlaceholderName: '- Deleted process -',
      availability: 'Availability',
      confirmTitle: 'Are you sure?',
      textWarningOnEnabling:
        'By enabling this process, you will allow the initiation of new runs via the UI or API and according to any existing schedules or triggers.',
      textWarningOnDisabling:
        'Disabling the process will prevent the start of new runs via the UI, API, schedules or triggers. However, any ongoing runs will continue as usual.',
      processIsDisabled: 'The process is disabled',
      flow: {
        done: '{{value}} done',
        inProgress: '{{value}} in progress',
        exceptions: '{{value}} failed',
        noExceptions: 'No failures',
      },
      wizard: {
        createProcess: 'Create Process',
        editProcess: 'Edit Process',
        saveChanges: 'Save Changes',
        advancedSettings: 'Advanced Settings',
        advancedSettingsDescription: 'Configure default input data and done item forwarding',
        wizardDescription: 'Configure a process to be used in unattended automation.',
        unsavedChanges: 'Unsaved changes',
        unsavedChangesDescription:
          'You have unsaved changes, are you sure you want to discard them?',
        discard: 'Discard',
        addSteps: 'Add Steps',
        addSchedule: 'Add Schedule',
        addNotification: 'Add Notifications',
        defaultRunTitle: 'Start Process with input data',
        defaultRunDescription: 'Default process run will be started with custom payload',
        configureStepsLater: 'Configure Steps Later',
        processCreateSuccess: 'Process has been created succesfully',
        processUpdateSuccess: 'Process has been updated succesfully',
        processCreateInProgress: 'Creating process',
      },
      disableReason: 'Reason',
      noReasonSpecified: 'No reason specified',
    },
    processRun: {
      title: 'Process run',
      plural: 'Process runs',
      run: 'Run',
      status: 'Status',
      runBy: 'Run by',
      runTime: 'Run time',
      totalWorkItemsShort: 'Total WI',
      totalWorkItems: 'Total Work Items',
      failedWorkItems: 'Failed Work Items',
      outputs: 'Outputs',
      failedWorkItemsShort: 'Failed WI',
      exceptions: 'Exceptions',
      duration: 'Lead time',
      state: 'Process run state',
      createTs: 'Created at',
      view: 'View Process Run',
      stopRun: {
        action: 'Stop Run',
        action_plural: 'Stop {{count}} Runs',
        title: 'Are you sure?',
        markPendingItemsLabel: `Mark "Pending" items as:`,
        description: 'Ongoing step runs will finish processing work items in progress.',
        description_plural:
          '{{count}} process runs will be stopped. Ongoing step runs will finish processing work items in progress.',
        terminateLabel: 'Terminate ongoing step runs instantly',
        successMessage: 'Process run stop initiated succesfully',
        timeline: {
          terminateOngoingStepRuns: 'terminate ongoing step runs',
          doNotTerminateOngoingStepRuns: 'do not terminate ongoing step runs',
          setPendingWorkItemsAsDone: 'set pending work items as "Done"',
          setPendingWorkItemsAsFailed: 'set pending work items as "Failed"',
          legacyStop: 'no conditions specified', // the stop mechanism has changed drastically, for backward compatibility we display a message: can be removed after 90 days (TTL)
        },
        reason: 'Reason for stopping the run',
      },
      deleteRun: 'Delete Run',
      flow: {
        done: '{{value}} done',
        inProgress: '{{value}} in progress',
        exceptions: '{{value}} failed',
        noExceptions: 'No failures',
      },
      scheduledRuns: 'Scheduled runs',
    },
    files: {
      title: 'Files',
      add: 'Add file',
      addMultiple: 'Add files',
      dropTitle: 'Drop Files fo Upload',
      processDescription: 'Upload files you want to run your process with.',
      workItemDescription: 'Upload files you want to run your work item with.',
      robotDescription:
        'Upload a .zip file containing the code for a tasks that follows the task package structure guidlines.',
      error: 'Invalid file type, accepts {{ types }} file',
      error_plural: 'Invalid file types, accepts {{ types }} file',
    },
    slack: {
      title: 'Slack Notifications',
      description: 'Get notified about unresolved or completed runs in Slack channels.',
      connectToSlack: 'Connect to Slack',
      selectChannel: 'Select a channel',
      disconnectFromSlack: 'Disconnect from Slack',
      succesfullLinking: 'Slack connected succesfully',
    },
    emailNotifications: {
      title: 'Email Notifications',
      description: 'Get notified about unresolved or completed runs by email.',
      removeEmail: 'Remove Email',
    },
    processSchedule: {
      title: 'Schedule the Process',
      description: 'Configure interval, weekday, or custom cron triggers for the process.',
      type: {
        interval: 'Interval',
        cron: 'Custom Cron Expression',
        weekday: 'Weekday',
      },
      allDays: 'All Days',
      intervalInSeconds: 'Interval in Seconds',
    },
    emailTrigger: {
      title: 'Trigger the Process with Email',
      description: 'Set up an email that will trigger the process.',
      emailDescription: 'Every new email to this address will trigger the process.',
      triggerProcess: 'Trigger Process',
      triggerProcessDescription:
        'Should process be triggered after incoming email. Work item will be created regardless of this value.',
      parseEmail: 'Parse Email',
      parseEmailDescription:
        'Work item created from the triggered email will have structured payload and attachments as work item files.',
      newProcessNote:
        'Email address will be generated and available after the process has been created',
    },
    explicitOutput: {
      title: 'Done Items Forwarding',
      description:
        'Pass done work items automatically from one step to the next. Useful for chaining tasks that are not designed for Control Room work item system.',
    },
    learnMore: 'Learn More',
    emptyState: {
      title: 'No results found',
      description: "There aren't any entries for {{ section }}.",
    },
    jsonEditor: {
      defaultData: 'Default data',
      editAsJSON: 'Edit JSON',
      duplicateKeyError: 'Duplicate Key',
      emptyKeyError: 'Empty Key',
      invalidJSONError: 'Invalid JSON',
    },
    businessId: 'Business ID',
    billingPrepaid: {
      usageRunMinutes: 'Run minutes',
      usageAssistantRuns: 'Assistant runs',
      usageRunMinutesForCurrentBillingPeriod: 'Run minutes for current billing period',
    },
    changeMethod: {
      panelHeader: 'Consumption plan settings',
      title: 'Collection method',
      button: 'Change',
      description: 'Configure whether you want to use credit card or invoice for future payments.',
      switch: 'Use Credit card',
      invoice: 'Invoice',
      card: 'Credit card',
      constraint: 'Add payment method first',
    },
    webhooks: {
      title: 'Webhooks',
      new: 'New Webhook',
      editSuccess: 'Webhook edited successfully',
      deleteSuccess: 'Webhook deleted successfully',
      testSuccess: 'Test event sent to endpoint',
      copySecret: 'Copy secret',
      eventType: 'Event',
      errorName: 'Error',
      errorCode: 'Error code',
      sentAt: 'Sent at',
      noLogs: 'No webhook logs found',
      logs: 'Webhook logs',
      process: 'Process',
      workspace: 'Workspace',
      processRun: 'Process run',
      showData: 'Show data',
      source: 'Resource',
      sendTestEvent: 'Send test event',
      filtersShowAll: 'Show all',
      filtersShowErrors: 'Show errors only',
      filtersShowSuccess: 'Show successfull only',
      showSecret: 'Show secret',
      testEvent: 'Test event',
      eventsToListenTo: 'Events to listen to',
      choose: 'Choose webhook',
    },
    processWebhooks: {
      title: 'Webhook Notifications',
      description:
        'Enter endpoints to use webhooks for process runs notifications. E.g. https://example.com/api/webhook',
      endpoint: 'Endpoint URL',
      endpointPlaceholder: 'Enter endpoint e.g. https://example.com/api/webhook',
      addWebhook: 'Add webhook',
      webhookAdded: 'Process webhook added',
      webhookAddError: 'Error adding webhook',
      webhookRemoved: 'Process webhook removed',
      webhookRemoveError: 'Error removing process webhook',
      sentOutgoingEvent: 'Sent Outgoing Event',
      sentOutgoingEventText: 'Event payload that was sent to the endpoint',
    },
    chart: {
      label: '{{from}} - {{to}}',
    },
    assetStorage: {
      title: 'Assets',
      noDataFound: 'No data found',
      editAsset: 'Edit asset',
      assetAdded: 'Asset added',
      assetUpdated: 'Asset modified',
      add: 'New asset',
      empty: 'Asset has no content',
      addDescription: 'Use assets to store data and access it by your processes and assistants.',
      aliasPlaceholder: 'Asset name',
      aliasDescription: 'Unique name for the asset',
      descriptionPlaceholder: 'What will this asset be used for?',
      uploading: 'Uploading asset content...',
      uploaded: 'Asset content uploaded!',
      uploadFailed: 'Asset content upload failed!',
      upload: 'Upload',
      uploadDescription: 'Update asset object with new content',
      download: 'Download',
      edit: 'Modify',
      editTitle: 'Modify Asset',
      editDescription: 'Use assets to store data and access it by your processes and assistants.',
      contentType: 'Type',
      contentSize: 'Content size',
      deleteAsset: 'Delete asset',
      deleteAssetText: 'Are you sure you want to delete {{ alias }} asset?',
      deleteAssetsText: 'Are you sure you want to delete {{ count }} asset?',
      assetDeleted: 'Asset deleted',
      assetsDeleted: 'Assets deleted',
      uploads: 'Asset log',
      assetContentUpdated: 'Asset content updated',
      downloadShouldStart: 'Download should start in a moment',
      dropzoneTitle: 'Drop files to upload',
      dropzoneDescription: 'Upload file you want to add to the asset',
      assetContent: {
        title: 'Asset content',
        [AssetContent.Text]: 'Text',
        [AssetContent.Json]: 'JSON',
        [AssetContent.File]: 'File',
        empty: 'Asset has no content',
        unknownType: 'Unknown content type',
        notSupported: 'Content type not supported',
        extensionFile: '.{{extension}} File',
        previewUnavailable: 'The preview is unavailable. To view the file, please download it.',
        dangerousPreview:
          'For security reason, the preview may not be displayed properly: download it to view it.',
      },
      uploadStatus: {
        [UploadStatus.UPLOAD_PENDING]: 'Pending',
        [UploadStatus.UPLOAD_DONE]: 'Completed',
        [UploadStatus.UPLOAD_FAILED]: 'Failed',
      },
      aliasAlreadyTaken: 'Name is already taken',
      maxStoredAssetsReached: 'Maximum number of assets reached {{ maxStoredAssets }}',
      delete: {
        action: 'Delete asset',
        action_plural: 'Delete {{ count }} assets',
      },
    },
    vault: {
      title: 'Vault',
      secretValuesObfuscationWarning:
        'Any values specified below will override the existing values',
      emptyValue: 'Empty value',
      secret: 'Secret',
      description: 'Manage your secrets with the secure Control Room Vault',
      new: 'New Secret',
      edit: 'Edit Secret',
      view: 'View Secret',
      secretName: 'Secret Name',
      secretNamePlaceholder: 'Type name',
      secretNameDescription: 'Provide an unique name without whitespaces or special characters.',
      secretDescription: 'Description',
      name: 'Name',
      items: 'Items',
      secretCreated: 'Secret was created',
      secretSaved: 'Secret was saved',
      delete: {
        title: 'Delete vault secret?',
        text: 'This will permanently remove vault secret',
        actionText: 'Delete',
      },
      error: {
        nameExists: 'Secret name already exists',
        whitespace: 'Secret name should not contain whitespaces or special characters',
        size: 'The value is too big',
        valueSize:
          'The item values are too big: consider using the <0>Asset Storage</0> if the values are not secrets',
        invalidVaultItemsValueShape:
          'This vault cannot be edited from Control Room due to invalid values for the following vault item(s): {{ vaultItems }}. To be able to edit this vault from Control Room, update your automation to save the vault item(s) as strings or manually update the value. Note that this may break your automations.',
      },
    },
    termsOfService: {
      title: 'Terms of Service',
      updateTitle: 'We`ve updated our Terms of Service',
      updateDescription:
        "We've updated our Terms of Service to ensure they are clear, understandable, and fair, while protecting your rights as a user. Please take a moment to review the new Terms of Service. The new terms will replace the old ones, unless you have a separate custom agreement with us. If you have any concerns or disagree with the changes, please reach out to us at support@robocorp.com.",
      agreeLabel: 'I have read and agree to the',
    },
    confirmationDialog: {
      title: 'Are you sure?',
    },
    robot: {
      title: 'Task Package',
      titlePlural: 'Task Packages',
      noItems: 'No Task Packages',
      add: 'Add Task Package',
      addTask: 'Add Task',
      edit: 'Edit Task Package',
      description: 'Add a Task from a Task Package to be executed by a Worker in this Step.',
      formDescription:
        'Manage your Task Packages in the Control Room and assign Tasks from them to run in Processes or Assistants.',
      name: 'Name',
      updatedTs: 'Updated',
      task: 'Task',
      taskPlural: 'Tasks',
      availableTasks: '{{ count }} available task',
      availableTasks_plural: '{{ count }} available tasks',
      stepRuns: 'Step Runs',
      processes: 'Processes',
      assistants: 'Assistants',
      origin: 'Origin',
      originDescription:
        'Assign an origin for your task package code, e.g., file or a version control system.',
      branch: 'Branch',
      commit: 'Commit',
      created: 'Created',
      lastModified: 'Last modified',
      modifiedBy: 'Modified by',
      cmdOnly: 'Command only',
      cmdOnlyInstructions:
        'Use “command only” when you want to run custom shell commands on the server.',
      file: 'Task Package',
      git: 'Public Git',
      gitInstructions: 'Supported formats',
      gitPlaceholder: 'URL of repository',
      github: 'GitHub App',
      gitlab: 'GitLab App',
      all: 'All',
      packageName: 'Task Package Name',
      packageNameDescription: 'Short and unique names will catch your attention better.',
      discoverExamples: 'Discover task package examples',
      manageIntegrations: 'Manage integrations from organization settings.',
      choose: 'Choose task package',
      chooseRun: 'Choose task run',
      chooseTask: 'Choose task',
      waitingForBundle: 'Processing task package file',
      failedToUploadBundle: 'Uploaded file is not valid',
      pinnedVersion: 'Pinned version',
      commitHashPlaceholder: 'Hash or tag',
      commitHashInstructions: 'The task package will point to the specific commit or tag',
      delete: {
        action: 'Delete task package',
        action_plural: 'Delete {{ count }} task packages',
        text: 'This will permanently remove task package',
        text_plural: 'This will permanently remove {{ count }} task packages',
      },
    },
    confirmation: {
      unsavedChanges: 'Unsaved changes',
      unsavedChangesDescription: 'You have unsaved changes, are you sure you want to discard them?',
      discard: 'Discard',
    },
    restriction: {
      notFound: {
        title: 'Not found',
        description: 'Content you tried to access does not exist.',
        instructions: 'Please make sure the link is correct.',
        workspace: {
          title: 'Workspace not found',
          description:
            'The workspace you tried to access does not exist or you do not have right to access it.',
          instructions:
            'Please make sure the link is correct and check with the workspace owner that it has been shared with you.',
        },
      },
      permission: {
        title: 'Restricted',
        description: 'You do not have correct access rights to view this content.',
      },
      error: {
        title: 'Oops... something went wrong',
        description: 'The engineering team has been notified.',
      },
    },
    activity: {
      title: 'Activity',
      empty: {
        runs: 'No ongoing activities',
        processRun: 'No ongoing process runs',
        scheduleRun: 'No ongoing schedule runs',
        stepRun: 'No ongoing step runs',
        assistantRun: 'No ongoing assistant runs',
        description:
          'Activity is the central hub where you can oversee and monitor all ongoing process runs and assistant runs in one place.',
      },
    },
    assistant: {
      title: 'Assistant',
    },
    assistantRun: {
      title: 'Assistant run',
      delete: {
        action: 'Delete assistant run',
        action_plural: 'Delete {{ count }} assistant runs',
        text: 'This will permanently delete assistant run',
        text_plural: 'This will permanently delete {{ count }} assistant runs',
      },
    },
    settings: {
      title: 'Settings',
      user: {
        title: 'Account Settings',
        changePassword: 'Change Password',
        managePassword: 'Manage password at <link />',
        darkMode: 'Dark mode',
        showKey: 'Show key',
        keyLimitReached: 'Credential key limit reached',
        addAccessCredential: 'Add Access Credential',
        delete: {
          account: {
            title: 'Delete user account',
            description:
              'Delete your private workspace and all workspaces where you are the only member. This action cannot be reverted.',
            confirmDescription:
              'Are you sure you want to delete your user account? This action will remove your private workspace and all other workspaces where you are the only member, including all workspace content. This action cannot be reverted.',
            confirmMessage: 'I want to delete my account',
            action: 'Delete Account',
          },
          credentials: {
            action: 'Delete credential',
            action_plural: 'Delete {{ count }} credentials',
            text: 'This will permanently remove this credential',
            text_plural: 'This will permanently remove {{ count }} credentials',
          },
        },
      },
      organization: {
        title: 'Organization Settings',
        timeFormat: 'Time Format',
        invite: {
          description: 'A person will receive an invitation to join this organization.',
          action: 'Send Invitation',
        },
      },
      workspace: {
        title: 'Workspace Settings',
        invite: {
          description: 'A person will receive an invitation to join this workspace.',
          action: 'Send Invitation',
        },
      },
    },
  },
};
